import { TypeManagerDecorator } from "../../main/type.map.service";
import { Semaphore } from "../../util/semaphore";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    effect,
    input,
    signal,
    ViewEncapsulation,
    ViewChildren,
    ViewChild,
    QueryList,
    ElementRef,
    output,
    linkedSignal,
    computed,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { ItemService } from "../../util/items/item.service";
import { S25Util } from "../../util/s25-util";
import { AlertService } from "../../services/alert.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import {
    IngestionFieldDefinition,
    IngestionFieldOption,
    IngestionFieldAnswer,
    IngestionSubmission,
    IngestionPoint,
    DataIngestionService,
    AnswerType,
    RegistrantsData,
    ObjectOccurrenceData,
    CategoriesData,
    ContactRoleData,
} from "./data.ingestion.service";
import {
    INGESTION_SYSTEM_FIELDS,
    STARS_NUMERIC_SCALE_MIN,
    STARS_NUMERIC_SCALE_MAX,
    IngestionPointCategory,
} from "./data.ingestion.const";
import { UserprefService } from "../../services/userpref.service";
import { EventFormRuleUtil } from "../s25-event-creation-form/s25.event.form.rule.util";
import { S25RuleTreeUtil } from "../s25-rule-tree/s25.rule.tree.util";
import { Rule, RuleTreeService } from "../../services/rule.tree.service";
import { AttachmentService } from "../../services/attachment.service";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { MasterDefinitionService } from "../../services/master.definitions/master.definition.service";
import { OrganizationService } from "../../services/organization.service";
import { ContactService } from "../../services/contact.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Item } from "../../pojo/Item";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25RichTextEditorModule } from "../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25ObjectOccurrenceFieldComponent } from "./field-types/s25.object.occurrence.field.component";
import { parseObjectOccurrenceFieldConfig } from "./field-types/object.occurrence.field.config";
import { parseCategoriesFieldConfig } from "./field-types/categories.field.config";
import { parseSectionFieldConfig } from "./field-types/section.field.config";
import { parseFormStyleConfig } from "./field-types/form.style.config";
import { resolveSuccessMessage } from "./field-types/ingestion.form.success-message";
import { ContactRoleFieldComponent } from "./field-types/s25.contact.role.field.component";
import { OrgContactRoleFieldComponent } from "./field-types/s25.org.contact.role.field.component";
import { DurationFieldComponent } from "./field-types/s25.duration.field.component";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { S25PipesModule } from "../../pipes/s25.pipes.module";
import { NameService } from "../../services/name.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { s25Resource } from "../../util/s25.resource";
import { ReservationService } from "../../services/reservation.service";
import { S25ToggleButtonModule } from "../s25-toggle-button/s25.toggle.button.module";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../services/state/state.map";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { ImageService } from "../../services/image.service";
import { S25ImageService } from "../s25-image/s25.image.service";
import { S25ImageUploadModule } from "../s25-image-upload/s25.image.upload.module";
import { FileStoreUtil } from "../../util/filestore.util";
import { FileUploadModule } from "../s25-file-upload/s25.file.upload.module";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { IngestionWorkflowAuditComponent } from "./ingestion.workflow.audit.component";
import { getAllowedPositionIdsForElection } from "./field-types/election.position.field.config";
import { ElectionApi } from "../student-engagement/elections/election.api";
import { S25FilestoreImageComponent } from "../../standalone/s25.filestore.image.component";
import {
    S25NgEventCreationFormWrapperComponent,
    isEmbeddedEventFormHandledError,
} from "../s25-event-creation-form/s25.event.creation.form.wrapper.component";
import { parseLive25EventFormFieldConfig } from "./field-types/live.25.event.form.field.config";
import { extractObjectOccurrenceSnapshot } from "./field-types/event.form.occurrence.snapshot";
import type { IngestionRuntimeHydrateFn } from "./ingestion.runtime.hydrate.context";
import { getHiddenFieldShowHints } from "./ingestion.form.rule.label.util";
import {
    formatCharCountRemaining,
    fieldUsesRichTextLength,
    getEffectiveMaxLength,
    isOverCharLimit,
    resolveFieldDefForLength,
    shouldShowCharCount,
} from "./ingestion.field.length.util";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { InstanceCount } from "../../util/s25.instance.count";

@TypeManagerDecorator("s25-ng-ingestion-form")
@Component({
    selector: "s25-ng-ingestion-form",
    template: `
        @if (init) {
            @let displayPoint = previewIngestionPoint() ?? ingestionPoint();
            @let formStyle = parseFormStyleConfig(displayPoint?.formStyle);
            <div class="c-margin-top--single">
                @if (formStyle.headerImageKey) {
                    <s25-ng-filestore-image
                        class="form-header-image"
                        [value]="formStyle.headerImageKey"
                        [readOnly]="true"
                    />
                }
                <h2
                    class="form-title c-margin-bottom--single"
                    [class.form-title--small]="formStyle.titleFontSize === 'small'"
                    [class.form-title--medium]="formStyle.titleFontSize === 'medium'"
                    [class.form-title--large]="formStyle.titleFontSize === 'large'"
                    [style.color]="formStyle.titleColor || null"
                >
                    {{ displayPoint?.ingestionPointName }}
                </h2>

                @if (submission()?.submitted) {
                    <div class="submission-status-alert">
                        <div class="submission-status-content">
                            <div class="submission-status-icon">
                                <i class="fa fa-check-circle"></i>
                            </div>
                            <div class="submission-status-text">
                                <div
                                    class="submission-success-message"
                                    [innerHTML]="resolveSuccessMessage(displayPoint?.successMessage) | safeHTML"
                                ></div>
                                @if (formatSubmissionDate(submission()!.submissionDate); as submittedOn) {
                                    <p class="submission-date">Submitted on {{ submittedOn }}</p>
                                }
                            </div>
                        </div>
                    </div>
                }

                @if (formValidationErrors && formValidationErrors.length > 0) {
                    <div
                        class="alert alert-danger c-margin-bottom--single"
                        role="alert"
                        aria-live="polite"
                        #formErrorContainer
                    >
                        <strong>Form Validation Errors:</strong>
                        <ul class="c-margin-bottom--none">
                            @for (error of formValidationErrors; track error) {
                                <li>{{ error }}</li>
                            }
                        </ul>
                    </div>
                }

                <div class="form-fields-grid">
                    @if (readOnly() && eventId()) {
                        <div class="form-field">
                            <label class="field-label">Event</label>
                            <a
                                s25-ng-router-link
                                [stateName]="StateNames.studentEngagementEventDetails"
                                [stateParams]="{ itemId: eventId() }"
                            >
                                Event details
                            </a>
                        </div>
                    }

                    @for (fieldDef of visibleFieldDefinitions(); track fieldDef) {
                        @if (!fieldDef.hidden) {
                            <div
                                [attr.data-field-id]="fieldDef.fieldDefinitionId"
                                [class.has-error]="fieldDef.validationError"
                                [class.full-width]="isFullWidthField(fieldDef)"
                                [attr.aria-invalid]="fieldDef.validationError ? 'true' : 'false'"
                            >
                                @if (fieldDef.fieldType === "SECTION") {
                                    @let sectionConfig = parseSectionFieldConfig(fieldDef.fieldConfig);
                                    @if (sectionConfig.headerImageKey) {
                                        <s25-ng-filestore-image
                                            class="section-header-image"
                                            [value]="sectionConfig.headerImageKey"
                                            [readOnly]="true"
                                        />
                                    }
                                    <h3
                                        class="section-heading c-margin-top--single c-margin-bottom--single"
                                        [class.section-heading--small]="sectionConfig.headingFontSize === 'small'"
                                        [class.section-heading--medium]="sectionConfig.headingFontSize === 'medium'"
                                        [class.section-heading--large]="sectionConfig.headingFontSize === 'large'"
                                        [style.color]="sectionConfig.headingColor || null"
                                    >
                                        {{ fieldDef.fieldLabel }}
                                        @if (fieldDef.infoText) {
                                            <span
                                                class="info-icon"
                                                (click)="toggleInfoText(fieldDef)"
                                                [title]="
                                                    'Click to ' +
                                                    (fieldDef.showInfoText ? 'hide' : 'show') +
                                                    ' additional information'
                                                "
                                            >
                                                ℹ
                                            </span>
                                        }
                                    </h3>
                                    @if (fieldDef.infoText && fieldDef.showInfoText) {
                                        <div
                                            class="field-info-text section-info"
                                            [innerHTML]="fieldDef.infoText | safeHTML"
                                        ></div>
                                    }
                                } @else {
                                    <div class="form-field">
                                        @if (
                                            fieldDef.fieldType !== "MULTISELECT_OCCURRENCES" ||
                                            !!occurrenceOptions()?.length
                                        ) {
                                            <label
                                                class="field-label"
                                                [attr.for]="
                                                    usesFieldsetLabel(fieldDef) ? null : fieldControlId(fieldDef)
                                                "
                                                [attr.id]="usesFieldsetLabel(fieldDef) ? fieldLabelId(fieldDef) : null"
                                            >
                                                <span class="field-label-caption">
                                                    {{ fieldDef.fieldLabel }}
                                                    @if (fieldDef.required) {
                                                        <span class="required-asterisk">*</span>
                                                    }
                                                    @if (fieldDef.infoText) {
                                                        <span
                                                            class="info-icon"
                                                            (click)="toggleInfoText(fieldDef)"
                                                            [title]="
                                                                'Click to ' +
                                                                (fieldDef.showInfoText ? 'hide' : 'show') +
                                                                ' additional information'
                                                            "
                                                            >ℹ</span
                                                        >
                                                    }
                                                </span>
                                            </label>
                                        }

                                        @if (fieldDef.infoText && fieldDef.showInfoText) {
                                            <div
                                                class="field-info-text"
                                                [innerHTML]="fieldDef.infoText | safeHTML"
                                            ></div>
                                        }
                                        @if (editMode() && fieldDef.requiresReapproval) {
                                            <div class="field-reapproval-warning">
                                                Editing this field will reset the workflow for re-approval.
                                            </div>
                                        }

                                        @if (fieldDef.fieldType === "SELECT") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    {{
                                                        fieldDef.singleAnswer?.option ||
                                                            fieldDef.longAnswer ||
                                                            "(No selection)"
                                                    }}
                                                </div>
                                            } @else {
                                                <select
                                                    class="c-selectInput cn-form__control"
                                                    [id]="fieldControlId(fieldDef)"
                                                    [(ngModel)]="fieldDef.singleAnswer"
                                                    (ngModelChange)="onAnswerChange(fieldDef)"
                                                    (blur)="validateField(fieldDef)"
                                                >
                                                    <option value="">-- Select an option --</option>
                                                    @for (opt of fieldDef.options; track opt) {
                                                        <option [ngValue]="opt">
                                                            {{ opt.option }}
                                                        </option>
                                                    }
                                                </select>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "STARS_RATING") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    @if (fieldDef.longAnswer) {
                                                        <div class="stars-rating-display">
                                                            @for (
                                                                i of getStarsArray(fieldDef);
                                                                track i;
                                                                let idx = $index
                                                            ) {
                                                                <span
                                                                    class="star"
                                                                    [class.filled]="
                                                                        idx < parseInt(fieldDef.longAnswer || '0', 10)
                                                                    "
                                                                >
                                                                    ★
                                                                </span>
                                                            }
                                                            <span class="stars-value">({{ fieldDef.longAnswer }})</span>
                                                        </div>
                                                    } @else {
                                                        (No rating)
                                                    }
                                                </div>
                                            } @else {
                                                <div
                                                    class="stars-rating-input"
                                                    role="group"
                                                    [attr.aria-labelledby]="fieldLabelId(fieldDef)"
                                                >
                                                    @for (i of getStarsArray(fieldDef); track i; let idx = $index) {
                                                        <span
                                                            class="star"
                                                            [class.filled]="idx < getStarsRating(fieldDef)"
                                                            [class.hover]="idx < hoveredStarIndex()"
                                                            (click)="setStarsRating(fieldDef, idx + 1)"
                                                            (mouseenter)="hoveredStarIndex.set(idx + 1)"
                                                            (mouseleave)="hoveredStarIndex.set(0)"
                                                            [title]="
                                                                'Rate ' + (idx + 1) + ' out of ' + getScaleMax(fieldDef)
                                                            "
                                                        >
                                                            ★
                                                        </span>
                                                    }
                                                    @if (fieldDef.longAnswer) {
                                                        <span class="stars-value">{{ fieldDef.longAnswer }}</span>
                                                    }
                                                </div>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "NUMERIC_SCALE") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    @if (fieldDef.longAnswer) {
                                                        {{ fieldDef.longAnswer }} out of {{ getScaleMax(fieldDef) }}
                                                    } @else {
                                                        (No selection)
                                                    }
                                                </div>
                                            } @else {
                                                <div class="numeric-scale-input">
                                                    <input
                                                        type="range"
                                                        class="scale-slider"
                                                        [id]="fieldControlId(fieldDef)"
                                                        [min]="getScaleMin(fieldDef)"
                                                        [max]="getScaleMax(fieldDef)"
                                                        step="1"
                                                        [value]="getNumericScaleValue(fieldDef)"
                                                        (input)="
                                                            setNumericScaleValue(fieldDef, $any($event.target).value)
                                                        "
                                                        (blur)="validateField(fieldDef)"
                                                    />
                                                    <div class="scale-ticks">
                                                        @for (
                                                            i of getScaleTickArray(fieldDef);
                                                            track i;
                                                            let idx = $index;
                                                            let count = $count
                                                        ) {
                                                            <div
                                                                class="tick"
                                                                [style.left.%]="
                                                                    count > 1 ? (idx / (count - 1)) * 100 : 50
                                                                "
                                                            >
                                                                <span class="tick-line"></span>
                                                                <span class="tick-value">{{ i }}</span>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "MULTISELECT") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    @if (fieldDef.answers && fieldDef.answers.length > 0) {
                                                        @for (
                                                            answer of fieldDef.answers;
                                                            track answer;
                                                            let last = $last
                                                        ) {
                                                            {{ answer.option }}{{ last ? "" : ", " }}
                                                        }
                                                    } @else {
                                                        (No selections)
                                                    }
                                                </div>
                                            } @else {
                                                <select
                                                    class="c-selectInput multiSelect cn-form__control"
                                                    [id]="fieldControlId(fieldDef)"
                                                    [(ngModel)]="fieldDef.answers"
                                                    multiple="multiple"
                                                    [attr.size]="getOptionsSelectSize(fieldDef.options)"
                                                    (ngModelChange)="onAnswerChange(fieldDef)"
                                                    (blur)="validateField(fieldDef)"
                                                >
                                                    @for (opt of fieldDef.options; track opt) {
                                                        <option [ngValue]="opt">
                                                            {{ opt.option }}
                                                        </option>
                                                    }
                                                </select>
                                                @if (fieldDef.answers && fieldDef.answers.length > 0) {
                                                    <button
                                                        type="button"
                                                        class="clear-all-btn"
                                                        (click)="clearAllSelections(fieldDef)"
                                                        title="Clear all selections"
                                                    >
                                                        Clear All
                                                    </button>
                                                }
                                            }
                                        }

                                        @if (
                                            fieldDef.fieldType === "MULTISELECT_OCCURRENCES" &&
                                            !!occurrenceOptions()?.length
                                        ) {
                                            @if (!isFieldReadOnly(fieldDef)) {
                                                <s25-ng-button
                                                    [type]="'outline'"
                                                    (clicked)="selectAllOccurrences(fieldDef)"
                                                >
                                                    Select All
                                                </s25-ng-button>
                                            }
                                            <div
                                                class="checkboxes"
                                                role="group"
                                                [attr.aria-labelledby]="fieldLabelId(fieldDef)"
                                            >
                                                @for (occ of occurrenceOptions(); track occ) {
                                                    <s25-ng-shift-selectable-checkbox
                                                        [group]="'MULTISELECT_OCCURRENCES'"
                                                        [index]="$index"
                                                        (blur)="validateField(fieldDef)"
                                                        [(value)]="occ.checked"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                        (valueChange)="onMultiselectOccsChange(fieldDef)"
                                                    >
                                                        {{ occ.label }}
                                                    </s25-ng-shift-selectable-checkbox>
                                                }
                                            </div>
                                        }

                                        @if (fieldDef.fieldType === "CHECKBOX") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    @if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                                        {{ getBooleanDisplayLabel(fieldDef) }}
                                                    } @else {
                                                        @if (fieldDef.answers && fieldDef.answers.length > 0) {
                                                            @for (
                                                                answer of fieldDef.answers;
                                                                track answer;
                                                                let last = $last
                                                            ) {
                                                                {{ answer.option }}{{ last ? "" : ", " }}
                                                            }
                                                        } @else {
                                                            (No selections)
                                                        }
                                                    }
                                                </div>
                                            } @else {
                                                <div role="group" [attr.aria-labelledby]="fieldLabelId(fieldDef)">
                                                    @if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                                        <s25-ng-checkbox
                                                            [value]="
                                                                fieldDef.longAnswer === 'true' ||
                                                                fieldDef.longAnswer === '1'
                                                            "
                                                            (valueChange)="
                                                                fieldDef.longAnswer = $event ? 'true' : 'false';
                                                                onAnswerChange(fieldDef)
                                                            "
                                                        >
                                                            {{ getBooleanOptionLabel(fieldDef) }}
                                                        </s25-ng-checkbox>
                                                    } @else {
                                                        <div class="checkbox-group">
                                                            @for (opt of fieldDef.options; track opt) {
                                                                <s25-ng-checkbox
                                                                    [value]="isOptionSelected(fieldDef, opt)"
                                                                    (valueChange)="
                                                                        onCheckboxOptionChange(fieldDef, opt, $event)
                                                                    "
                                                                >
                                                                    {{ opt.option }}
                                                                </s25-ng-checkbox>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "RADIOBUTTON") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    @if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                                        {{ getBooleanDisplayLabel(fieldDef) }}
                                                    } @else {
                                                        {{ fieldDef.singleAnswer?.option || "(No selection)" }}
                                                    }
                                                </div>
                                            } @else {
                                                <div
                                                    class="radiobutton-group"
                                                    role="group"
                                                    [attr.aria-labelledby]="fieldLabelId(fieldDef)"
                                                >
                                                    @if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                                        @for (
                                                            opt of getBooleanOptions(fieldDef);
                                                            track opt.option;
                                                            let i = $index
                                                        ) {
                                                            <s25-ng-radio
                                                                [(value)]="fieldDef.longAnswer"
                                                                [radioValue]="opt.option"
                                                                [name]="'radiobutton-' + fieldDef.fieldDefinitionId"
                                                                (valueChange)="onAnswerChange(fieldDef)"
                                                                [ariaLabel]="
                                                                    getBooleanDisplayLabel(fieldDef, opt.option)
                                                                "
                                                            >
                                                                {{ getBooleanDisplayLabel(fieldDef, opt.option) }}
                                                            </s25-ng-radio>
                                                        }
                                                    } @else {
                                                        @for (opt of fieldDef.options; track opt; let i = $index) {
                                                            <s25-ng-radio
                                                                [value]="fieldDef.singleAnswer"
                                                                [radioValue]="opt"
                                                                [name]="'radiobutton-' + fieldDef.fieldDefinitionId"
                                                                (valueChange)="
                                                                    fieldDef.singleAnswer = $event;
                                                                    onAnswerChange(fieldDef)
                                                                "
                                                                [ariaLabel]="opt.option"
                                                            >
                                                                {{ opt.option }}
                                                            </s25-ng-radio>
                                                        }
                                                    }
                                                </div>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "TEXT") {
                                            @if (fieldDef.answerType === AnswerType.LARGE_STRING) {
                                                @if (isFieldReadOnly(fieldDef)) {
                                                    <div
                                                        class="rich-text-display"
                                                        [innerHTML]="fieldDef.longAnswer | safeHTML"
                                                    ></div>
                                                } @else {
                                                    <s25-ng-rich-text-editor
                                                        [rteLabel]="''"
                                                        [(modelValue)]="fieldDef.longAnswer"
                                                        (modelValueChange)="onAnswerChange(fieldDef)"
                                                    ></s25-ng-rich-text-editor>
                                                }
                                            } @else if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                                <s25-toggle-button
                                                    [toggleLabel]="fieldDef.fieldLabel"
                                                    [value]="
                                                        fieldDef.longAnswer === 'true' || fieldDef.longAnswer === '1'
                                                    "
                                                    (valueChange)="
                                                        fieldDef.longAnswer = $event.toString();
                                                        onAnswerChange(fieldDef)
                                                    "
                                                    [disabled]="isFieldReadOnly(fieldDef)"
                                                />
                                            } @else {
                                                <input
                                                    class="c-input"
                                                    [id]="fieldControlId(fieldDef)"
                                                    [type]="getInputType(fieldDef.answerType)"
                                                    [(ngModel)]="fieldDef.longAnswer"
                                                    (ngModelChange)="onAnswerChange(fieldDef)"
                                                    (blur)="validateField(fieldDef)"
                                                    [attr.min]="fieldDef.minLength ?? null"
                                                    [attr.max]="fieldDef.maxLength ?? null"
                                                    [disabled]="isFieldReadOnly(fieldDef)"
                                                />
                                            }
                                        }

                                        @if (fieldDef.fieldType === "RICH_TEXT") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div
                                                    class="rich-text-display"
                                                    [innerHTML]="fieldDef.longAnswer | safeHTML"
                                                ></div>
                                            } @else {
                                                <s25-ng-rich-text-editor
                                                    [rteLabel]="''"
                                                    [(modelValue)]="fieldDef.longAnswer"
                                                    (modelValueChange)="onAnswerChange(fieldDef)"
                                                ></s25-ng-rich-text-editor>
                                            }
                                        }

                                        @if (fieldDef.fieldType === "FILE") {
                                            <s25-ng-editable-file
                                                [model]="getFileModel(fieldDef)"
                                                [canEdit]="!isFieldReadOnly(fieldDef)"
                                                [excludeCustAttrFiles]="true"
                                            ></s25-ng-editable-file>
                                        }

                                        @if (fieldDef.fieldType === "IMAGE") {
                                            <s25-ng-filestore-image
                                                [value]="fieldDef.longAnswer || ''"
                                                (valueChange)="fieldDef.longAnswer = $event; onAnswerChange(fieldDef)"
                                                [readOnly]="isFieldReadOnly(fieldDef)"
                                                [objectType]="-1"
                                                [objectId]="-1"
                                                [fileKey]="imageFieldFileKey(fieldDef)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "ELECTION_POSITION") {
                                            @if (previewMode()) {
                                                <div class="preview-live25-placeholder form-control-static">
                                                    This field requires an election context and cannot be previewed
                                                    here. Test it via the Nominations page.
                                                </div>
                                            } @else if (isFieldReadOnly(fieldDef)) {
                                                <div class="form-control-static">
                                                    {{ getElectionPositionReadonlyLabel(fieldDef) }}
                                                </div>
                                            } @else {
                                                @if (!fieldDef.hidden) {
                                                    <s25-ng-dropdown-search-criteria
                                                        [(value)]="fieldDef.specialFieldItem"
                                                        (valueChange)="
                                                            onElectionPositionDropdownChange(fieldDef, $event)
                                                        "
                                                        [type]="'electionPositionsForElection'"
                                                        [customFilterValue]="getElectionPositionSearchFilter(fieldDef)"
                                                        [filter]="electionPositionItemFilter(fieldDef)"
                                                        [readOnly]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                            }
                                        }

                                        @if (fieldDef.fieldType === "LIVE_25_EVENT_FORM" && !fieldDef.hidden) {
                                            @if (previewMode()) {
                                                <div class="preview-live25-placeholder form-control-static">
                                                    The 25Live event form will appear here.
                                                </div>
                                            } @else {
                                                <s25-ng-event-creation-form-wrapper
                                                    #liveEventFormWrapper
                                                    [hideSave]="true"
                                                    [skipBuiltInPostSaveNavigation]="true"
                                                    [hideAfterSavingThisEventSection]="true"
                                                    [allowProgrammaticSave]="true"
                                                    [forceDraft]="!isEventEditForm()"
                                                    [configIdOverride]="
                                                        parseLive25EventFormFieldConfig(fieldDef)
                                                            .eventFormConfigurationId ?? undefined
                                                    "
                                                    [eventId]="live25EventFormEventId(fieldDef) ?? undefined"
                                                    [mode]="live25EventFormEventId(fieldDef) ? 'edit' : 'create'"
                                                    [previewOnly]="isFieldReadOnly(fieldDef)"
                                                    (onFormSave)="onLive25EventFormSave(fieldDef, $event)"
                                                ></s25-ng-event-creation-form-wrapper>
                                            }
                                        }

                                        @if (isSpecialFieldType(fieldDef.fieldType) && !fieldDef.hidden) {
                                            <s25-ng-dropdown-search-criteria
                                                [(value)]="fieldDef.specialFieldItem"
                                                (valueChange)="onAnswerChange(fieldDef)"
                                                [type]="getSearchCriteriaType(fieldDef.fieldType)"
                                                [readOnly]="isFieldReadOnly(fieldDef)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "CATEGORIES" && !fieldDef.hidden) {
                                            @let catConfig = categoriesFieldConfig(fieldDef);
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'eventCategories'"
                                                [allowedItemIds]="catConfig.categoryIds"
                                                [singleSelect]="catConfig.singleSelect"
                                                [selectedItems]="$any(fieldDef.jsonFieldItem)?.items || []"
                                                (changed)="onCategoriesChange(fieldDef, $event)"
                                                [showResults]="true"
                                                [usePopover]="!catConfig.expandAutomatically"
                                                [buttonText]="
                                                    catConfig.expandAutomatically
                                                        ? undefined
                                                        : catConfig.buttonLabel || undefined
                                                "
                                                [disabled]="isFieldReadOnly(fieldDef)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "ORG_CATEGORIES" && !fieldDef.hidden) {
                                            @let orgCatConfig = categoriesFieldConfig(fieldDef);
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'organizationCategories'"
                                                [allowedItemIds]="orgCatConfig.categoryIds"
                                                [singleSelect]="orgCatConfig.singleSelect"
                                                [selectedItems]="$any(fieldDef.jsonFieldItem)?.items || []"
                                                (changed)="onCategoriesChange(fieldDef, $event)"
                                                [showResults]="true"
                                                [usePopover]="!orgCatConfig.expandAutomatically"
                                                [buttonText]="
                                                    orgCatConfig.expandAutomatically
                                                        ? undefined
                                                        : orgCatConfig.buttonLabel || undefined
                                                "
                                                [disabled]="isFieldReadOnly(fieldDef)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "REQUIREMENTS" && !fieldDef.hidden) {
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'eventRequirements'"
                                                [selectedItems]="$any(fieldDef.jsonFieldItem)?.items || []"
                                                (changed)="onRequirementsChange(fieldDef, $event)"
                                                [showResults]="true"
                                                [disabled]="isFieldReadOnly(fieldDef)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "ORGANIZATION_MULTISELECT" && !fieldDef.hidden) {
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'organizations'"
                                                [selectedItems]="$any(fieldDef.jsonFieldItem)?.items || []"
                                                (changed)="onOrgMultiselectChange(fieldDef, $event)"
                                                [showResults]="true"
                                                [disabled]="isFieldReadOnly(fieldDef)"
                                            />
                                        }

                                        @if (
                                            fieldDef.fieldType === "CUSTOM_ATTRIBUTE" ||
                                            fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE"
                                        ) {
                                            @switch (fieldDef.custAtrb?.atrbDataType) {
                                                @case ("B") {
                                                    <s25-toggle-button
                                                        [toggleLabel]="fieldDef.fieldLabel"
                                                        [value]="
                                                            fieldDef.longAnswer === 'true' ||
                                                            fieldDef.longAnswer === '1'
                                                        "
                                                        (valueChange)="
                                                            fieldDef.longAnswer = $event.toString();
                                                            onAnswerChange(fieldDef)
                                                        "
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("N") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="number"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("F") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="number"
                                                        step="0.01"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("S") {
                                                    @if (
                                                        getDiscreteOptions(fieldDef).length &&
                                                        !fieldDef.custAtrb?.multiVal
                                                    ) {
                                                        @if (isFieldReadOnly(fieldDef)) {
                                                            <div class="read-only-value">
                                                                {{ fieldDef.longAnswer || "(No selection)" }}
                                                            </div>
                                                        } @else {
                                                            <select
                                                                class="c-input cn-form__control"
                                                                [id]="fieldControlId(fieldDef)"
                                                                [(ngModel)]="fieldDef.longAnswer"
                                                                (ngModelChange)="onAnswerChange(fieldDef)"
                                                                (blur)="validateField(fieldDef)"
                                                            >
                                                                <option value="">-- Select an option --</option>
                                                                @for (
                                                                    opt of getDiscreteOptions(fieldDef);
                                                                    track opt.itemId
                                                                ) {
                                                                    <option [value]="opt.itemId">
                                                                        {{ opt.itemName }}
                                                                    </option>
                                                                }
                                                            </select>
                                                        }
                                                    } @else if (
                                                        getDiscreteOptions(fieldDef).length &&
                                                        fieldDef.custAtrb?.multiVal
                                                    ) {
                                                        @if (isFieldReadOnly(fieldDef)) {
                                                            <div class="read-only-value">
                                                                {{ getMultiValDisplayText(fieldDef) }}
                                                            </div>
                                                        } @else {
                                                            <select
                                                                class="c-input cn-form__control multiSelect"
                                                                [id]="fieldControlId(fieldDef)"
                                                                multiple
                                                                [attr.size]="getMultiSelectSize(fieldDef)"
                                                                [ngModel]="getMultiValSelectedValues(fieldDef)"
                                                                (ngModelChange)="
                                                                    setMultiValFromSelectedValues(fieldDef, $event)
                                                                "
                                                                (blur)="validateField(fieldDef)"
                                                            >
                                                                @for (
                                                                    opt of getDiscreteOptions(fieldDef);
                                                                    track opt.itemId
                                                                ) {
                                                                    <option [value]="opt.itemId">
                                                                        {{ opt.itemName }}
                                                                    </option>
                                                                }
                                                            </select>
                                                        }
                                                    } @else {
                                                        <input
                                                            class="c-input"
                                                            [id]="fieldControlId(fieldDef)"
                                                            type="text"
                                                            [(ngModel)]="fieldDef.longAnswer"
                                                            (ngModelChange)="onAnswerChange(fieldDef)"
                                                            [disabled]="isFieldReadOnly(fieldDef)"
                                                        />
                                                    }
                                                }
                                                @case ("X") {
                                                    <textarea
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                        rows="4"
                                                    ></textarea>
                                                }
                                                @case ("D") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="date"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("E") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="datetime-local"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("T") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="time"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("L") {
                                                    <input
                                                        class="c-input"
                                                        [id]="fieldControlId(fieldDef)"
                                                        type="time"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                                @case ("R") {
                                                    @if (fieldDef.custAtrbId === -20 || fieldDef.custAtrbId === -21) {
                                                        <s25-ng-image-upload
                                                            [imageUri]="getUrlImagePreview(fieldDef)"
                                                            [hasCropper]="false"
                                                            [showPreview]="true"
                                                            [previewWidth]="400"
                                                            [showClear]="true"
                                                            (uploadedImageChange)="onUrlImageUploaded(fieldDef, $event)"
                                                            (clear)="clearCustomAttrImage(fieldDef)"
                                                        ></s25-ng-image-upload>
                                                    } @else if (
                                                        fieldDef.custAtrbId === -2 || fieldDef.custAtrbId === -1
                                                    ) {
                                                        @if (isFieldReadOnly(fieldDef)) {
                                                            @if (fieldDef.longAnswer) {
                                                                @let href = sanitizedUrl(fieldDef.longAnswer);
                                                                @if (href) {
                                                                    <a
                                                                        [href]="href"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        Link to URL
                                                                    </a>
                                                                }
                                                            }
                                                        } @else {
                                                            <input
                                                                class="c-input"
                                                                [id]="fieldControlId(fieldDef)"
                                                                type="text"
                                                                [(ngModel)]="fieldDef.longAnswer"
                                                                (ngModelChange)="onAnswerChange(fieldDef)"
                                                                [disabled]="isFieldReadOnly(fieldDef)"
                                                                maxlength="250"
                                                                placeholder="Enter a URL"
                                                            />
                                                        }
                                                    } @else {
                                                        @if (isFieldReadOnly(fieldDef)) {
                                                            @if (FileStoreUtil.isFileStoreUrl(fieldDef.longAnswer)) {
                                                                @let downloadUrl =
                                                                    FileStoreUtil.getDownloadUrl(fieldDef.longAnswer);
                                                                @let fileStoreParams =
                                                                    FileStoreUtil.parseFileStoreUrl(
                                                                        fieldDef.longAnswer
                                                                    );
                                                                @if (downloadUrl) {
                                                                    <a
                                                                        [href]="downloadUrl"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {{ fileStoreParams?.fileName ?? "Link to URL" }}
                                                                    </a>
                                                                }
                                                            } @else if (fieldDef.longAnswer) {
                                                                @let href = sanitizedUrl(fieldDef.longAnswer);
                                                                @if (href) {
                                                                    <a
                                                                        [href]="href"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        Link to URL
                                                                    </a>
                                                                }
                                                            }
                                                        } @else {
                                                            <s25-ng-file-upload
                                                                [multiple]="false"
                                                                [showFiles]="false"
                                                                [onAdd]="getUrlFileUploadHandler(fieldDef)"
                                                            ></s25-ng-file-upload>
                                                            @if (FileStoreUtil.isFileStoreUrl(fieldDef.longAnswer)) {
                                                                @let downloadUrl =
                                                                    FileStoreUtil.getDownloadUrl(fieldDef.longAnswer);
                                                                @let fileStoreParams =
                                                                    FileStoreUtil.parseFileStoreUrl(
                                                                        fieldDef.longAnswer
                                                                    );
                                                                @if (downloadUrl) {
                                                                    <div class="c-margin-top--quarter">
                                                                        <a
                                                                            [href]="downloadUrl"
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {{
                                                                                fileStoreParams?.fileName ??
                                                                                    "Link to URL"
                                                                            }}
                                                                        </a>
                                                                    </div>
                                                                }
                                                            }
                                                            <input
                                                                class="c-input c-margin-top--quarter"
                                                                type="text"
                                                                [(ngModel)]="fieldDef.longAnswer"
                                                                (ngModelChange)="onAnswerChange(fieldDef)"
                                                                maxlength="250"
                                                                placeholder="Or enter a URL"
                                                            />
                                                        }
                                                    }
                                                }
                                                @case ("I") {
                                                    <s25-ng-image-upload
                                                        [imageUri]="getCustomAttrImagePreview(fieldDef)"
                                                        [hasCropper]="false"
                                                        [showPreview]="true"
                                                        [previewWidth]="400"
                                                        [showClear]="true"
                                                        (uploadedImageChange)="
                                                            onCustomAttrImageUploaded(fieldDef, $event)
                                                        "
                                                        (clear)="clearCustomAttrImage(fieldDef)"
                                                    ></s25-ng-image-upload>
                                                }
                                                @default {
                                                    <input
                                                        class="c-input"
                                                        type="text"
                                                        [(ngModel)]="fieldDef.longAnswer"
                                                        (ngModelChange)="onAnswerChange(fieldDef)"
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    />
                                                }
                                            }
                                        }

                                        @if (fieldDef.fieldType === "DURATION") {
                                            <s25-ng-duration-field
                                                [value]="fieldDef.longAnswer"
                                                [readOnly]="isFieldReadOnly(fieldDef)"
                                                (valueChange)="onDurationChange(fieldDef, $event)"
                                            />
                                        }

                                        @if (fieldDef.fieldType === "TOGGLE") {
                                            @if (isFieldReadOnly(fieldDef)) {
                                                <div class="read-only-value">
                                                    {{
                                                        fieldDef.longAnswer === "true"
                                                            ? fieldDef.options?.[0]?.option || "Yes"
                                                            : fieldDef.options?.[1]?.option || "No"
                                                    }}
                                                </div>
                                            } @else {
                                                <s25-toggle-button
                                                    [toggleLabel]="fieldDef.fieldLabel"
                                                    [value]="fieldDef.longAnswer === 'true'"
                                                    [trueLabel]="fieldDef.options?.[0]?.option || 'Yes'"
                                                    [falseLabel]="fieldDef.options?.[1]?.option || 'No'"
                                                    (valueChange)="
                                                        fieldDef.longAnswer = $event ? 'true' : 'false';
                                                        onAnswerChange(fieldDef)
                                                    "
                                                />
                                            }
                                        }

                                        @if (isJsonField(fieldDef)) {
                                            @if (fieldDef.fieldType === "OBJECT_OCCURRENCE" && !fieldDef.hidden) {
                                                <s25-ng-object-occurrence-field
                                                    [value]="$any(fieldDef.jsonFieldItem)"
                                                    [readOnly]="isFieldReadOnly(fieldDef)"
                                                    [showLocations]="objectOccurrenceUi(fieldDef).showLocations"
                                                    [showResources]="objectOccurrenceUi(fieldDef).showResources"
                                                    (valueChange)="onOccurrenceFieldChange(fieldDef, $event)"
                                                ></s25-ng-object-occurrence-field>
                                            }

                                            @if (fieldDef.fieldType === "CONTACT_ROLE" && !fieldDef.hidden) {
                                                <s25-ng-contact-role-field
                                                    [value]="$any(fieldDef.jsonFieldItem)"
                                                    [readOnly]="isFieldReadOnly(fieldDef)"
                                                    (valueChange)="onContactRoleChange(fieldDef, $event)"
                                                />
                                            }

                                            @if (fieldDef.fieldType === "ORG_CONTACT_ROLE" && !fieldDef.hidden) {
                                                <s25-ng-org-contact-role-field
                                                    [value]="$any(fieldDef.jsonFieldItem)"
                                                    [readOnly]="isFieldReadOnly(fieldDef)"
                                                    (valueChange)="onContactRoleChange(fieldDef, $event)"
                                                />
                                            }

                                            @if (
                                                fieldDef.fieldType === "REGISTRANTS" &&
                                                !!fieldDef.jsonFieldItem &&
                                                !fieldDef.hidden
                                            ) {
                                                <div class="registrants">
                                                    <select
                                                        class="cn-form__control"
                                                        [id]="fieldControlId(fieldDef)"
                                                        [(ngModel)]="$any(fieldDef.jsonFieldItem).type"
                                                        (ngModelChange)="
                                                            $any(fieldDef.jsonFieldItem).items = [];
                                                            onAnswerChange(fieldDef)
                                                        "
                                                        [disabled]="isFieldReadOnly(fieldDef)"
                                                    >
                                                        <option [ngValue]="'everyone'">Everyone</option>
                                                        <option [ngValue]="'contacts'">Contacts</option>
                                                        <option [ngValue]="'orgRoles'">Organization Roles</option>
                                                    </select>
                                                    @if ($any(fieldDef.jsonFieldItem)?.type === "contacts") {
                                                        <s25-ng-multiselect-search-criteria
                                                            [type]="'contacts'"
                                                            [selectedItems]="$any(fieldDef.jsonFieldItem).items || []"
                                                            (changed)="onAnswerChange(fieldDef)"
                                                            [showResults]="true"
                                                            [disabled]="isFieldReadOnly(fieldDef)"
                                                        />
                                                    } @else if ($any(fieldDef.jsonFieldItem)?.type === "orgRoles") {
                                                        <s25-ng-multiselect-search-criteria
                                                            [type]="'organizationRoles'"
                                                            [selectedItems]="$any(fieldDef.jsonFieldItem).items || []"
                                                            (changed)="onAnswerChange(fieldDef)"
                                                            [showResults]="true"
                                                            [disabled]="isFieldReadOnly(fieldDef)"
                                                        />
                                                    }
                                                </div>
                                            }
                                        }

                                        @if (shouldShowFieldCharCount(fieldDef)) {
                                            <small
                                                class="form-text"
                                                [class.char-count--over]="isFieldAnswerOverCharLimit(fieldDef)"
                                            >
                                                {{ formatFieldCharCountMessage(fieldDef) }}
                                            </small>
                                        }

                                        @if (fieldDef.validationError) {
                                            <div
                                                class="validation-error"
                                                role="alert"
                                                aria-live="polite"
                                                [attr.id]="'error-' + fieldDef.fieldDefinitionId"
                                                #validationError
                                            >
                                                {{ fieldDef.validationError }}
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        } @else if (showHiddenFieldPlaceholders() && fieldDef.fieldType !== "SECTION") {
                            <div
                                class="preview-hidden-field-placeholder"
                                [attr.data-field-id]="fieldDef.fieldDefinitionId"
                            >
                                <span class="preview-hidden-field-label">{{
                                    fieldDef.fieldLabel || "Untitled field"
                                }}</span>
                                <span class="preview-hidden-field-hint">{{
                                    hiddenFieldHintByFieldId().get(fieldDef.fieldDefinitionId!) ?? ""
                                }}</span>
                            </div>
                        }
                    }
                </div>

                @if (submission()?.submissionIdUuid && submission()?.submitted && ingestionPoint() && readOnly()) {
                    <s25-ng-ingestion-workflow-audit
                        [submissionIdUuid]="submission()!.submissionIdUuid"
                        [ingestionPoint]="ingestionPoint()!"
                    ></s25-ng-ingestion-workflow-audit>
                }

                @if (!previewMode()) {
                    <div
                        class="form-actions"
                        [class.form-actions--embedded-live25-event-form]="hasEmbeddedLive25EventForm()"
                    >
                        @if (!readOnly() && !editMode()) {
                            @if (showFormExitActions()) {
                                @if (showDiscardAction()) {
                                    <s25-ng-button
                                        [type]="'danger--outline'"
                                        [onClick]="cancelForm"
                                        [buttonClass]="'c-margin-right--single'"
                                    >
                                        Cancel
                                    </s25-ng-button>
                                }
                                @if (!hasEmbeddedLive25EventForm()) {
                                    <s25-ng-button
                                        [type]="'outline'"
                                        [onClick]="saveProgressAndClose"
                                        [buttonClass]="'c-margin-right--single'"
                                    >
                                        Save Progress & Close
                                    </s25-ng-button>
                                }
                            } @else if (!hasEmbeddedLive25EventForm()) {
                                <s25-ng-button
                                    [type]="'outline'"
                                    [onClick]="saveProgressOnly"
                                    [buttonClass]="'c-margin-right--single'"
                                >
                                    Save Progress
                                </s25-ng-button>
                            }

                            <s25-ng-button [type]="'primary'" [onClick]="validateAndSubmit">
                                Submit Form
                            </s25-ng-button>
                        }
                        @if (editMode()) {
                            <s25-ng-button [type]="'primary'" [onClick]="saveEditedAnswers">
                                Save Changes
                            </s25-ng-button>
                            @if (editSaveMessage()) {
                                <span class="edit-save-message">{{ editSaveMessage() }}</span>
                            }
                        }
                    </div>
                }

                @if (canOfferSeedDraft()) {
                    <div class="fork-seed-quiet c-margin-top--single">
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Start a new draft using these answers…'"
                            [onClick]="openForkSeedModal"
                        />
                    </div>
                }

                @if (forkSeedModalOpen()) {
                    <div class="fork-seed-backdrop" (click)="onForkSeedBackdropClick($event)">
                        <div
                            class="fork-seed-dialog"
                            (click)="$event.stopPropagation()"
                            role="dialog"
                            aria-modal="true"
                        >
                            <h3 class="fork-seed-dialog-title">Start a pre-filled draft</h3>
                            <p class="fork-seed-dialog-copy">
                                Answers are copied only where fields match between forms.
                                <strong>This form</strong> starts a new draft on this same form (you can continue right
                                away). Choose <strong>Another form</strong> only when the destination is different; then
                                pick the form below before continuing.
                            </p>
                            <div class="fork-seed-choice c-margin-bottom--single">
                                <label class="fork-seed-radio">
                                    <input
                                        type="radio"
                                        name="forkWhich"
                                        [checked]="!forkUseAnotherForm()"
                                        (change)="onForkSameFormChoice()"
                                    />
                                    This form
                                </label>
                                <label class="fork-seed-radio">
                                    <input
                                        type="radio"
                                        name="forkWhich"
                                        [checked]="forkUseAnotherForm()"
                                        (change)="onForkOtherFormChoice()"
                                    />
                                    Another form
                                </label>
                            </div>
                            @if (forkUseAnotherForm()) {
                                <div class="form-field">
                                    <label class="field-label">Form</label>
                                    <s25-ingestion-point-dropdown
                                        [category]="forkCategoryEngagement"
                                        [subCat]="$safeNavigationMigration(ingestionPoint()?.subCat)"
                                        [enabledOnly]="true"
                                        [excludeIngestionPointId]="submission()?.ingestionPointId ?? undefined"
                                        [filter]="forkIngestionTargetFilter"
                                        [value]="forkTargetFormSelection()"
                                        (valueChange)="forkTargetFormSelection.set($event)"
                                    />
                                </div>
                            }
                            <div class="fork-seed-dialog-actions c-margin-top--single">
                                <s25-ng-button
                                    [type]="'danger--outline'"
                                    [buttonText]="'Cancel'"
                                    [onClick]="closeForkSeedModal"
                                    [buttonClass]="'c-margin-right--single'"
                                />
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="'Continue'"
                                    [disabled]="forkConfirmDisabled()"
                                    [disabledReason]="forkContinueDisabledReason()"
                                    [isLoading]="forkSeedBusy()"
                                    [onClick]="confirmForkSeed"
                                />
                            </div>
                        </div>
                    </div>
                }
            </div>
        } @else {
            <s25-ng-loading-inline-static />
        }
    `,
    styles: `
        .multiSelect {
            height: auto;
            overflow: auto;
        }

        .clear-all-btn {
            margin-top: 0.5rem;
            padding: 0.25rem 0.75rem;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 3px;
            color: #6c757d;
            font-size: 0.875rem;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .clear-all-btn:hover {
            background-color: #e9ecef;
            border-color: #adb5bd;
            color: #495057;
        }

        .clear-all-btn:active {
            background-color: #dee2e6;
            border-color: #6c757d;
        }

        .form-field {
            margin-bottom: 1rem;
        }

        .preview-hidden-field-placeholder {
            margin-bottom: 1rem;
            padding: 0.75rem 1rem;
            border: 1px dashed #adb5bd;
            border-radius: 4px;
            background-color: #f1f3f5;
            color: #6c757d;
        }

        .preview-hidden-field-label {
            display: block;
            font-weight: 600;
            margin-bottom: 0.25rem;
        }

        .preview-hidden-field-hint {
            display: block;
            font-size: 0.875rem;
            font-style: italic;
        }

        .preview-live25-placeholder {
            padding: 0.75rem 1rem;
            border: 1px dashed #adb5bd;
            border-radius: 4px;
            background-color: #f8f9fa;
            color: #6c757d;
            font-style: italic;
        }

        .field-label {
            display: block;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }

        .field-label-caption {
            display: inline;
        }

        .form-title {
            color: var(--cn-blue, #0066cc);
            font-weight: 600;
        }

        /* !important overrides global body.s25-ng h2 sizing */
        .form-title.form-title--small {
            font-size: 1rem !important;
        }

        .form-title.form-title--medium {
            font-size: 1.25rem !important;
        }

        .form-title.form-title--large {
            font-size: 1.6rem !important;
        }

        .form-header-image {
            display: block;
            margin-bottom: 0.5rem;
        }

        .form-header-image ::ng-deep .fs-image__preview {
            max-width: 100%;
            max-height: 240px;
        }

        .submission-success-message {
            margin-bottom: 0.25rem;
        }

        .section-heading {
            color: var(--cn-blue, #0066cc);
            font-weight: 600;
        }

        /* !important overrides global body.s25-ng h3 { font-size: 1.1rem !important } */
        .section-heading.section-heading--small {
            font-size: 0.875rem !important;
        }

        .section-heading.section-heading--medium {
            font-size: 1.1rem !important;
        }

        .section-heading.section-heading--large {
            font-size: 1.4rem !important;
        }

        .section-header-image {
            display: block;
            margin-bottom: 0.5rem;
        }

        .section-header-image ::ng-deep .fs-image__preview {
            max-width: 100%;
            max-height: 200px;
        }

        .required-asterisk {
            color: var(--color--text-danger);
            margin-left: 2px;
        }

        .info-icon {
            margin-left: 5px;
            color: #2573a7;
            cursor: pointer;
            font-weight: bold;
            padding: 2px 6px;
            border-radius: 50%;
            background-color: rgba(37, 115, 167, 0.1);
            transition: background-color 0.2s ease;
        }

        .info-icon:hover {
            background-color: rgba(37, 115, 167, 0.2);
        }

        .field-info-text {
            font-size: 0.9em;
            color: #666;
            font-style: italic;
            margin-bottom: 0.5rem;
            padding: 0.75rem;
            background-color: #f8f9fa;
            border-left: 3px solid #2573a7;
            border-radius: 3px;
            animation: fadeIn 0.3s ease-in-out;
        }

        .field-info-text.section-info {
            margin-top: 0.5rem;
            margin-bottom: 1rem;
        }

        .edit-save-message {
            color: #155724;
            background-color: #d4edda;
            border: 1px solid #c3e6cb;
            padding: 0.35rem 0.75rem;
            border-radius: 3px;
            font-size: 0.9em;
            margin-left: 0.5rem;
            display: inline-block;
        }

        .field-reapproval-warning {
            font-size: 0.85em;
            color: #856404;
            background-color: #fff3cd;
            border-left: 3px solid #ffc107;
            padding: 0.4rem 0.75rem;
            margin-bottom: 0.5rem;
            border-radius: 3px;
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
                max-height: 0;
                padding-top: 0;
                padding-bottom: 0;
            }
            to {
                opacity: 1;
                max-height: 200px;
                padding-top: 0.75rem;
                padding-bottom: 0.75rem;
            }
        }

        .validation-error {
            color: var(--color--text-danger);
            font-size: 0.9em;
            margin-top: 0.25rem;
        }

        .form-text {
            color: var(--color--text-muted);
            font-size: 12px;
            margin-top: 4px;
        }

        .char-count--over {
            color: var(--color--text-danger);
        }

        .alert {
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 4px;
        }

        .alert-success {
            background-color: #d4edda;
            border-color: #c3e6cb;
            color: #155724;
        }

        .form-actions {
            margin-top: 2rem;
            border-top: 1px solid #ddd;
            padding-top: 1rem;
        }

        /* Match embedded 25Live form body offset (s25-event-form-nav.scss) so sticky nav does not cover Submit. */
        .form-actions--embedded-live25-event-form {
            padding-left: 18%;
        }

        @media screen and (max-width: 1200px) {
            .form-actions--embedded-live25-event-form {
                padding-left: 23%;
            }
        }

        @media screen and (max-width: 900px) {
            .form-actions--embedded-live25-event-form {
                padding-left: 0;
            }
        }

        .rich-text-display {
            min-height: 2.5rem;
            padding: 0.75rem;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background-color: var(--color--surface-2-bg);
            color: var(--color--text);
            line-height: 1.5;
        }

        .rich-text-display:empty::before {
            content: "No content";
            color: var(--color--text-muted);
            font-style: italic;
        }

        .submission-status-alert {
            background-color: #d4edda;
            border: 1px solid #c3e6cb;
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1.5rem;
            overflow: hidden;
            position: relative;
        }

        .submission-status-content {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .submission-status-icon {
            color: #28a745;
            font-size: 1.5rem;
            flex-shrink: 0;
        }

        .submission-status-text {
            flex: 1;
        }

        .submission-status-text strong {
            color: #155724;
            font-size: 1.1rem;
            display: block;
            margin-bottom: 0.25rem;
        }

        .submission-date {
            color: #155724;
            margin: 0;
            font-size: 0.9rem;
        }

        .registrants {
            display: grid;
            row-gap: 0.5rem;
        }

        .checkbox-group {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .radiobutton-group {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .read-only-value {
            padding: 0.5rem;
            min-height: 2rem;
            display: flex;
            align-items: center;
            color: #333;
        }

        .stars-rating-input,
        .stars-rating-display {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .star {
            font-size: 1.5rem;
            color: #ddd;
            cursor: pointer;
            transition: color 0.2s ease;
            user-select: none;
        }

        .star.filled {
            color: #ffc107;
        }

        .star.hover {
            color: #ffd54f;
        }

        .stars-value {
            margin-left: 0.5rem;
            font-weight: bold;
            color: #666;
        }

        /* Numeric Scale */
        .numeric-scale-input {
            --thumb-width: 1.25rem;
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .scale-slider {
            width: 100%;
            height: 0.5rem;
            border-radius: 0.25rem;
            background: #ddd;
            outline: none;
            -webkit-appearance: none;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        .scale-slider::-webkit-slider-runnable-track {
            height: 0.5rem;
            border-radius: 0.25rem;
            background: #ddd;
        }

        .scale-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: var(--thumb-width);
            height: var(--thumb-width);
            border-radius: 50%;
            background: #2573a7;
            cursor: pointer;
            margin-top: calc((var(--thumb-width) - 0.5rem) / -2);
        }

        .scale-slider::-moz-range-track {
            height: 0.5rem;
            border-radius: 0.25rem;
            background: #ddd;
            border: none;
        }

        .scale-slider::-moz-range-thumb {
            width: var(--thumb-width);
            height: var(--thumb-width);
            border-radius: 50%;
            background: #2573a7;
            cursor: pointer;
            border: none;
            box-sizing: border-box;
        }

        .checkboxes {
            display: grid;
            gap: 0.5rem;
        }

        /* Visual tick marks - absolutely positioned for precise alignment */
        .scale-ticks {
            position: relative;
            width: calc(100% - var(--thumb-width));
            height: 1.5rem;
            margin: 0.5rem calc(var(--thumb-width) / 2) 0;
        }

        .scale-ticks .tick {
            position: absolute;
            transform: translateX(-50%);
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .scale-ticks .tick-line {
            width: 2px;
            height: 0.5rem;
            background: #999;
        }

        .scale-ticks .tick-value {
            font-size: 0.75rem;
            color: #666;
            margin-top: 0.25rem;
        }

        .form-fields-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem 1.5rem;
        }

        .form-fields-grid .form-field {
            margin-bottom: 0;
        }

        .form-fields-grid > * {
            min-width: 0;
        }

        .form-fields-grid > .full-width {
            grid-column: 1 / -1;
        }

        .form-fields-grid > .preview-hidden-field-placeholder {
            grid-column: 1 / -1;
        }

        .form-fields-grid .form-field {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        .form-fields-grid .form-field .field-label {
            min-height: 2.5em;
            display: flex;
            align-items: flex-end;
        }

        .form-fields-grid .form-field input,
        .form-fields-grid .form-field select,
        .form-fields-grid .form-field textarea,
        .form-fields-grid .form-field .c-input,
        .form-fields-grid .form-field .c-selectInput {
            width: 100%;
            box-sizing: border-box;
        }

        .form-fields-grid .form-field textarea.c-input {
            height: auto;
            min-height: 6em;
        }

        @media screen and (max-width: 480px) {
            :host s25-ng-object-occurrence-field ::ng-deep .datetime-group {
                flex: 1;
            }
            .form-fields-grid {
                grid-template-columns: 1fr;
            }

            .form-fields-grid .form-field .field-label {
                min-height: unset;
            }

            .form-actions {
                display: flex;
            }

            .form-actions s25-ng-button {
                flex: 1;
            }
        }

        .fork-seed-quiet {
            font-size: 0.9rem;
        }

        .fork-seed-backdrop {
            position: fixed;
            inset: 0;
            z-index: 1050;
            background: rgba(0, 0, 0, 0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
        }

        .fork-seed-dialog {
            background: #fff;
            border-radius: 6px;
            max-width: 420px;
            width: 100%;
            padding: 1.25rem 1.5rem;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
        }

        .fork-seed-dialog-title {
            margin-top: 0;
            margin-bottom: 0.5rem;
            font-size: 1.1rem;
            color: #2573a7;
        }

        .fork-seed-dialog-copy {
            margin-bottom: 0.75rem;
            color: #555;
            font-size: 0.9rem;
        }

        .fork-seed-choice {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
        }

        .fork-seed-radio {
            font-weight: normal;
            cursor: pointer;
        }

        .fork-seed-dialog-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;
        }

        .fork-seed-empty {
            color: #666;
            font-size: 0.9rem;
            margin: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormsModule,
        S25ButtonComponent,
        S25RichTextEditorModule,
        S25EditableModule,
        S25DropdownPaginatedModule,
        S25ObjectOccurrenceFieldComponent,
        ContactRoleFieldComponent,
        OrgContactRoleFieldComponent,
        DurationFieldComponent,
        S25MultiselectModule,
        S25PipesModule,
        S25ToggleButtonModule,
        S25RouterLinkDirective,
        S25CheckboxModule,
        S25RadioModule,
        S25ImageUploadModule,
        FileUploadModule,
        IngestionWorkflowAuditComponent,
        S25FilestoreImageComponent,
        S25NgEventCreationFormWrapperComponent,
        S25LoadingInlineStaticComponent,
    ],
})
export class IngestionFormComponent extends InstanceCount implements OnInit {
    submissionIdUuid = input<string>();
    ingestionPointId = input<number>();
    readOnly = input<boolean>(false);
    /** When set, invoked after `applyDefaultValues` during `refresh` (skipped when `readOnly`). Return `true` if answers changed and progress should be saved. */
    runtimeHydrate = input<IngestionRuntimeHydrateFn | undefined>(undefined);
    editMode = input<boolean>(false);
    eventIdInput = input<number>(undefined, { alias: "eventId" });
    electionIdInput = input<number>(undefined, { alias: "electionId" });
    /** When true, submitter may copy answers into a new draft (fork); only ingestion dashboard enables this. */
    allowAnswerSeedToNewDraft = input<boolean>(false);
    /** When true, show Cancel and Save Progress & Close (draft/dashboard flows). Link/UUID flows leave this false. Cancel deletes new drafts; for existing drafts it reverts in-session changes. */
    readonly showFormExitActions = input<boolean>(false);
    readonly hiddenOccurrences = input<Set<number> | undefined>(undefined);
    /** Form-builder preview: in-memory field defs and rules; no submission create/save. */
    readonly previewMode = input<boolean>(false);
    readonly previewIngestionPoint = input<IngestionPoint | undefined>(undefined);
    readonly previewFieldDefinitions = input<IngestionFieldDefinition[] | undefined>(undefined);
    readonly previewShowHideRules = input<Rule[] | undefined>(undefined);
    readonly showHiddenFieldPlaceholders = input<boolean>(false);

    private ingestionService = inject(DataIngestionService);
    private itemService = inject(ItemService);

    // Linked signals that sync with inputs but can also be updated internally
    private _submissionIdUuid = linkedSignal(() => this.submissionIdUuid());
    private _ingestionPointId = linkedSignal(() => this.ingestionPointId());

    onClose = output<void>();
    onSubmit = output<{ submissionIdUuid: string; answers: IngestionFieldAnswer[]; successMessage?: string | null }>();
    /** Emitted after a successful in-place reload when the user forks to a new draft (dashboard syncs bindings). */
    forkContextCreated = output<IngestionSubmission>();

    private saveSemaphore = new Semaphore();

    @ViewChildren("validationError", { read: ElementRef }) validationErrors!: QueryList<ElementRef>;
    @ViewChild("formErrorContainer", { read: ElementRef }) formErrorContainer?: ElementRef;

    /** Embedded 25Live event-form wrappers, one per LIVE_25_EVENT_FORM field. */
    @ViewChildren("liveEventFormWrapper") liveEventFormWrappers!: QueryList<S25NgEventCreationFormWrapperComponent>;

    // Expose to template
    AnswerType = AnswerType;
    FileStoreUtil = FileStoreUtil;
    S25Util = S25Util;
    /** Exposed to the template for the LIVE_25_EVENT_FORM admin-chosen config override. */
    readonly parseLive25EventFormFieldConfig = parseLive25EventFormFieldConfig;
    readonly parseSectionFieldConfig = parseSectionFieldConfig;
    readonly parseFormStyleConfig = parseFormStyleConfig;
    readonly resolveSuccessMessage = resolveSuccessMessage;

    readonly ingestionPoint = signal<IngestionPoint | undefined>(undefined);
    readonly submission = signal<IngestionSubmission | undefined>(undefined);
    readonly fieldDefinitions = signal<IngestionFieldDefinition[] | undefined>(undefined);
    readonly editSaveMessage = signal<string>("");
    showHideRules: Rule[];
    validationRules: Rule[];
    origFieldHiddenState = new Map<number, boolean>();
    formValidationErrors: string[] = [];
    init = false;
    saving = false;
    dateTimeFormat = "MM/dd/yyyy 'at' h:mm a";
    contId: number;
    private lastIngestionPointId?: number;
    private lastSubmissionIdUuid?: string;
    private isRefreshing = false;
    /** When true, Cancel deletes the draft; when false, Cancel reverts to {@link sessionBaselineAnswers}. */
    private cancelShouldDeleteDraft = true;
    /** Answers as loaded when the host opened an existing draft; used to revert in-session edits on Cancel. */
    private sessionBaselineAnswers: IngestionFieldAnswer[] | null = null;
    submitted = false;

    readonly eventId = computed(() => {
        // Get event ID from input, submission (authless registration links), or EVENT system field answer
        if (!!this.eventIdInput()) return this.eventIdInput();
        const submissionEventId = this.submission()?.eventId;
        if (submissionEventId != null) return submissionEventId;
        const eventField = this.fieldDefinitions()?.find(
            (field) => field.fieldDefinitionId === INGESTION_SYSTEM_FIELDS.EVENT,
        );
        if (!eventField) return undefined;
        const num = eventField.specialFieldItem?.itemId as number;
        return isNaN(num) ? undefined : num;
    });

    readonly electionId = computed(() => {
        if (!!this.electionIdInput()) {
            return this.electionIdInput();
        }
        const submissionElectionId = this.submission()?.electionId;
        if (submissionElectionId) {
            return submissionElectionId;
        }
        return undefined;
    });

    readonly isEventEditForm = computed(() => Boolean(this.ingestionPoint()?.use?.eventEdit));
    readonly isRegistrationForm = computed(() => Boolean(this.ingestionPoint()?.use?.registration));
    readonly showDiscardAction = computed(() => this.showFormExitActions() && !this.isRegistrationForm());
    readonly occurrences = s25Resource({
        params: () => {
            if (!this.eventId()) return undefined;
            if (!this.fieldDefinitions()?.some((f) => f.fieldType === "MULTISELECT_OCCURRENCES")) return undefined;
            return this.eventId();
        },
        loader: async ({ params: eventId, abortSignal }) => {
            return ReservationService.getEventReservations(eventId);
        },
    });
    readonly occurrenceOptions = computed(() => {
        const field = this.multiselectOccurrencesField();
        const selected = new Set(field?.answers?.map((answer) => answer.option) ?? []);
        const hiddenOccs = this.hiddenOccurrences() ?? new Set();
        return this.occurrences
            .value()
            ?.filter((occ) => !hiddenOccs.has(occ.reservation_id))
            ?.map((occ) => ({
                option: String(occ.reservation_id),
                label: this.formatSubmissionDate(occ.event_start_dt),
                checked: selected.has(String(occ.reservation_id)),
            }));
    });

    readonly hiddenFieldHintByFieldId = computed(() => {
        if (!this.showHiddenFieldPlaceholders()) {
            return new Map<number, string>();
        }

        const fieldDefinitions = this.fieldDefinitions() ?? [];
        const rules = this.previewMode() ? (this.previewShowHideRules() ?? []) : (this.showHideRules ?? []);
        const hintByFieldId = new Map<number, string>();

        for (const fieldDef of fieldDefinitions) {
            if (!fieldDef.fieldDefinitionId) {
                continue;
            }
            const hints = getHiddenFieldShowHints(fieldDef.fieldDefinitionId, rules, fieldDefinitions);
            if (hints.length) {
                hintByFieldId.set(fieldDef.fieldDefinitionId, hints.join("; "));
            }
        }

        return hintByFieldId;
    });

    isFieldReadOnly(fieldDef: IngestionFieldDefinition): boolean {
        return this.readOnly() || !!fieldDef.readOnly;
    }

    isFullWidthField(fieldDef: IngestionFieldDefinition): boolean {
        const fullWidthTypes: Set<string> = new Set([
            "SECTION",
            "RICH_TEXT",
            "MULTISELECT_OCCURRENCES",
            "OBJECT_OCCURRENCE",
            "FILE",
            "IMAGE",
            "REGISTRANTS",
            "CATEGORIES",
            "ORG_CATEGORIES",
            "REQUIREMENTS",
            "ORGANIZATION_MULTISELECT",
            "CONTACT_ROLE",
            "ORG_CONTACT_ROLE",
            "LIVE_25_EVENT_FORM",
        ]);
        if (fullWidthTypes.has(fieldDef.fieldType!)) return true;
        if (fieldDef.fieldType === "TEXT" && fieldDef.answerType === AnswerType.LARGE_STRING) return true;
        return false;
    }

    /**
     * Returns the persisted draft event id for a LIVE_25_EVENT_FORM field (from the field's
     * own integer answer, falling back to the canonical SYSTEM_FIELD_EVENT_ID / submission.eventId
     * so existing event-id readers and a freshly-edit-revisited submission both work).
     */
    live25EventFormEventId(fieldDef: IngestionFieldDefinition): number | null {
        const raw = this.trimLongAnswer(fieldDef.longAnswer);
        const eventId = raw ? parseInt(raw, 10) : NaN;
        if (!isNaN(eventId)) return eventId;
        const fallback = this.eventId();
        return typeof fallback === "number" ? fallback : null;
    }

    /** Called when the embedded 25Live form completes any save (initial draft or subsequent edits). */
    onLive25EventFormSave = (fieldDef: IngestionFieldDefinition, eventId: number) => {
        fieldDef.longAnswer = String(eventId);
    };

    /**
     * Drives the embedded 25Live event-form save before the Connect25 submission save.
     * Event creation uses saveDraft; event edit uses save (real event state).
     */
    private persistEmbeddedLive25EventForm = async () => {
        const wrapper = this.liveEventFormWrappers?.first;
        if (!wrapper) return;

        const fieldDef = this.visibleFieldDefinitions().find(
            (def) => def.fieldType === "LIVE_25_EVENT_FORM" && !def.hidden,
        );
        if (!fieldDef || this.isFieldReadOnly(fieldDef)) return;

        const savedEventId = this.isEventEditForm() ? await wrapper.save({}) : await wrapper.saveDraft({});
        fieldDef.longAnswer = String(savedEventId);
    };

    objectOccurrenceUi(fieldDef: IngestionFieldDefinition) {
        return parseObjectOccurrenceFieldConfig(fieldDef.fieldConfig);
    }

    categoriesFieldConfig(fieldDef: IngestionFieldDefinition) {
        return parseCategoriesFieldConfig(fieldDef.fieldConfig);
    }

    /** Fields to display: exclude isDeleted unless the field has answers mapped. */
    readonly visibleFieldDefinitions = computed(() => {
        const defs = this.fieldDefinitions();
        if (!defs) return [];
        return defs.filter((fieldDef) => !fieldDef.isDeleted || fieldDef.answersMapped);
    });

    /**
     * True when this ingestion form embeds the native 25Live event form. Connect25 draft
     * save / answer-seed flows only persist Connect25 field answers, not 25Live form state.
     */
    readonly hasEmbeddedLive25EventForm = computed(() =>
        (this.fieldDefinitions() ?? []).some(
            (fieldDef) => fieldDef.fieldType === "LIVE_25_EVENT_FORM" && !fieldDef.isDeleted,
        ),
    );

    // Signal for tracking hovered star index (for stars rating UI)
    readonly hoveredStarIndex = signal<number>(0);

    readonly forkSeedModalOpen = signal(false);
    readonly forkUseAnotherForm = signal(false);
    readonly forkTargetFormSelection = signal<DropDownItem<number> | null>(null);
    readonly forkSeedBusy = signal(false);

    readonly forkCategoryEngagement = IngestionPointCategory.ENGAGEMENT;

    readonly forkIngestionTargetFilter = (item: DropDownItem<number>) => {
        const point = item.data as IngestionPoint | undefined;
        return point ? point.active !== false : true;
    };

    readonly canOfferSeedDraft = computed(() => {
        if (!this.allowAnswerSeedToNewDraft()) {
            return false;
        }
        if (this.hasEmbeddedLive25EventForm()) {
            return false;
        }
        const submission = this.submission();
        const point = this.ingestionPoint();
        if (!this.init || !submission?.submissionIdUuid || !point) {
            return false;
        }
        if (point.authless) {
            return false;
        }
        if (!this.contId || !submission.contId) {
            return false;
        }
        return submission.contId === this.contId;
    });

    readonly forkConfirmDisabled = computed(() => {
        if (!this.forkUseAnotherForm()) {
            return false;
        }
        const selection = this.forkTargetFormSelection();
        const targetId = selection?.itemId ?? selection?.val;
        return targetId == null;
    });

    readonly forkContinueDisabledReason = computed(() =>
        this.forkConfirmDisabled()
            ? "Select a destination form, or switch to “This form” for a new draft on this form."
            : "",
    );

    /** Discrete options by custom attribute ID (for CUSTOM_ATTRIBUTE type S with saved options) */
    discreteOptionsByAttrId: Record<number, { itemId: string; itemName: string }[]> = {};
    /** Precomputed options per field (avoids getDiscreteOptionsForField in template - fixes CD loop/timeout) */
    discreteOptionsByFieldId = new Map<number, { itemId: string; itemName: string }[]>();
    /** Precomputed multi-val selected values per field (stable reference avoids ngModel CD loop) */
    multiValSelectedValuesByFieldId = new Map<number, string[]>();
    /** Set in {@link assignDiscreteOptionsMapKeys}; avoids indexOf on every template read. */
    private discreteOptionsMapKeyByFieldDef = new WeakMap<IngestionFieldDefinition, number>();
    /** Below {@link INGESTION_SYSTEM_FIELDS} (-1..-4) so unsaved preview keys never collide. */
    private static readonly DISCRETE_OPTIONS_UNSAVED_KEY_BASE = -100_000;

    readonly multiselectOccurrencesField = computed(() => {
        return this.fieldDefinitions()?.find((def) => def.fieldType === "MULTISELECT_OCCURRENCES");
    });

    constructor(public cd: ChangeDetectorRef) {
        super();
        // Watch for changes to submissionIdUuid or ingestionPointId and refresh the form
        effect(() => {
            if (this.previewMode()) {
                return;
            }

            // Skip if we're already refreshing (to avoid loops when setting signals internally)
            if (this.isRefreshing) {
                return;
            }

            const submissionIdUuid = this._submissionIdUuid();
            const ingestionPointId = this._ingestionPointId();

            // Only refresh if component has been initialized and inputs have values
            // Skip refresh if neither has changed (to avoid infinite loops)
            if (this.init && (submissionIdUuid || ingestionPointId)) {
                const submissionIdUuidChanged = submissionIdUuid !== this.lastSubmissionIdUuid;
                const ingestionPointIdChanged = ingestionPointId !== this.lastIngestionPointId;
                if (submissionIdUuidChanged || ingestionPointIdChanged) {
                    this.lastSubmissionIdUuid = submissionIdUuid;
                    this.lastIngestionPointId = ingestionPointId;
                    this.refresh();
                }
            }
        });

        effect(() => {
            // When occurrence options load, update any occurrence answers
            const options = this.occurrenceOptions();
            const field = this.multiselectOccurrencesField();
            if (!options || !field) return;
            const optionMap = new Map(options.map((option) => [option.option, option]));
            field.answers = (field.answers ?? []).map((answer) => optionMap.get(answer.option!) ?? answer);
            for (const answer of <(typeof options)[number][]>field.answers) answer.checked = true;
        });
    }

    async ngOnInit() {
        this.dateTimeFormat = await UserprefService.getS25DateTimeformat();
        this.contId = await UserprefService.getContactId();
        if (this.previewMode()) {
            await this.loadPreviewConfig();
            return;
        }
        this.lastSubmissionIdUuid = this.submissionIdUuid();
        this.lastIngestionPointId = this.ingestionPointId();
        this.refresh();
    }

    formatSubmissionDate = (dateString: string | undefined): string => {
        if (!dateString) return "";
        return S25Datefilter.transform(dateString, this.dateTimeFormat) || "";
    };

    private assignDiscreteOptionsMapKeys(fieldDefs: IngestionFieldDefinition[]): void {
        for (let listIndex = 0; listIndex < fieldDefs.length; listIndex++) {
            const fieldDef = fieldDefs[listIndex];
            if (
                (fieldDef.fieldType !== "CUSTOM_ATTRIBUTE" && fieldDef.fieldType !== "ORG_CUSTOM_ATTRIBUTE") ||
                fieldDef.custAtrb?.atrbDataType !== "S"
            ) {
                continue;
            }
            const mapKey =
                fieldDef.fieldDefinitionId != null
                    ? fieldDef.fieldDefinitionId
                    : IngestionFormComponent.DISCRETE_OPTIONS_UNSAVED_KEY_BASE - listIndex;
            this.discreteOptionsMapKeyByFieldDef.set(fieldDef, mapKey);
        }
    }

    /** Map key for discrete-option caches; assigned during {@link ensureDiscreteOptionsForAllFields}. */
    private discreteOptionsMapKey(fieldDef: IngestionFieldDefinition): number | undefined {
        const cached = this.discreteOptionsMapKeyByFieldDef.get(fieldDef);
        if (cached != null) {
            return cached;
        }
        if (fieldDef.fieldDefinitionId != null) {
            return fieldDef.fieldDefinitionId;
        }
        return undefined;
    }

    /** Compute and store discrete options for a CUSTOM_ATTRIBUTE field (ANG-6524: prepend current value if not in list).
     * Called on load and when user changes value - never from template (avoids CD loop). */
    private ensureDiscreteOptionsForField(fieldDef: IngestionFieldDefinition): void {
        if (
            (fieldDef.fieldType !== "CUSTOM_ATTRIBUTE" && fieldDef.fieldType !== "ORG_CUSTOM_ATTRIBUTE") ||
            fieldDef.custAtrb?.atrbDataType !== "S"
        ) {
            return;
        }
        const mapKey = this.discreteOptionsMapKey(fieldDef);
        if (mapKey == null) {
            return;
        }
        const base = this.discreteOptionsByAttrId[fieldDef.custAtrbId ?? 0] ?? [];
        if (!base.length) {
            this.discreteOptionsByFieldId.set(mapKey, []);
            return;
        }
        const optionsSet = new Set(base.map((o) => o.itemId));
        const toItem = (val: string) => ({ itemId: val, itemName: val });
        const missing: { itemId: string; itemName: string }[] = [];
        if (!fieldDef.custAtrb?.multiVal && fieldDef.longAnswer && !optionsSet.has(fieldDef.longAnswer)) {
            missing.push(toItem(fieldDef.longAnswer));
        }
        if (fieldDef.custAtrb?.multiVal && fieldDef.answers?.length) {
            for (const a of fieldDef.answers) {
                const val = a?.option;
                if (val && !optionsSet.has(val)) {
                    missing.push(toItem(val));
                    optionsSet.add(val);
                }
            }
        }

        // pre-compute the result associated with the field definition id to avoid infinite change detection loop
        const result = missing.length ? [...missing, ...base] : base;
        this.discreteOptionsByFieldId.set(mapKey, result);
        if (fieldDef.custAtrb?.multiVal) {
            const selected = (fieldDef.answers ?? []).map((a) => a?.option).filter(Boolean) as string[];
            const existing = this.multiValSelectedValuesByFieldId.get(mapKey);
            if (!existing || existing.length !== selected.length || selected.some((v, i) => v !== existing[i])) {
                this.multiValSelectedValuesByFieldId.set(mapKey, selected);
            }
        }
    }

    private ensureDiscreteOptionsForAllFields(): void {
        this.discreteOptionsByFieldId.clear();
        this.multiValSelectedValuesByFieldId.clear();
        const fieldDefs = this.fieldDefinitions() ?? [];
        this.assignDiscreteOptionsMapKeys(fieldDefs);
        for (const fieldDef of fieldDefs) {
            if (
                (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") &&
                fieldDef.custAtrb?.atrbDataType === "S"
            ) {
                const mapKey = this.discreteOptionsMapKey(fieldDef);
                if (mapKey == null) {
                    continue;
                }
                this.discreteOptionsByFieldId.set(mapKey, []);
                if (fieldDef.custAtrb?.multiVal) {
                    this.multiValSelectedValuesByFieldId.set(mapKey, []);
                }
            }
        }
        for (const fieldDef of fieldDefs) {
            this.ensureDiscreteOptionsForField(fieldDef);
        }
    }

    /** Discrete options for field; always populated during ensureDiscreteOptionsForAllFields. */
    getDiscreteOptions(fieldDef: IngestionFieldDefinition): { itemId: string; itemName: string }[] {
        const mapKey = this.discreteOptionsMapKey(fieldDef);
        if (mapKey == null) {
            return [];
        }
        return this.discreteOptionsByFieldId.get(mapKey) ?? [];
    }

    getMultiValSelectedValues(fieldDef: IngestionFieldDefinition): string[] {
        const mapKey = this.discreteOptionsMapKey(fieldDef);
        if (mapKey == null) {
            return [];
        }
        return this.multiValSelectedValuesByFieldId.get(mapKey) ?? [];
    }

    /** Visible rows for multi-select; scales with option count (min 4, max 15). */
    getMultiSelectSize(fieldDef: IngestionFieldDefinition): number {
        const opts = this.getDiscreteOptions(fieldDef);
        return Math.min(Math.max(4, opts.length), 15);
    }

    getOptionsSelectSize(options: { length: number } | undefined): number {
        const len = options?.length ?? 0;
        return Math.min(Math.max(4, len), 15);
    }

    setMultiValFromSelectedValues(fieldDef: IngestionFieldDefinition, values: string[]) {
        const selected = (values ?? []).filter(Boolean);
        fieldDef.answers = selected.map((v) => ({ option: v }));
        const mapKey = this.discreteOptionsMapKey(fieldDef);
        if (mapKey != null) {
            this.multiValSelectedValuesByFieldId.set(mapKey, selected);
        }
        this.ensureDiscreteOptionsForField(fieldDef);
        this.onAnswerChange(fieldDef);
    }

    getMultiValDisplayText(fieldDef: IngestionFieldDefinition): string {
        const parts = this.getMultiValSelectedValues(fieldDef);
        return parts.length ? parts.join(", ") : "(No selections)";
    }

    /** Election id for {@link SearchCriteriaType} {@code electionPositionsForElection} (runtime caller context). */
    getElectionPositionSearchFilter(fieldDef: IngestionFieldDefinition): string {
        const runtimeElectionId = this.electionId();
        if (runtimeElectionId) {
            return String(runtimeElectionId);
        }
        return "";
    }

    electionPositionItemFilter(fieldDef: IngestionFieldDefinition): (item: DropDownItem) => boolean {
        const allowedPositionIds = getAllowedPositionIdsForElection(fieldDef, this.electionId());
        if (!allowedPositionIds?.length) {
            return () => true;
        }
        const allowedSet = new Set(allowedPositionIds);
        return (item: DropDownItem) => {
            const positionId = Number(item.itemId);
            return Number.isFinite(positionId) && allowedSet.has(positionId);
        };
    }

    onElectionPositionDropdownChange(fieldDef: IngestionFieldDefinition, value: DropDownItem | null) {
        fieldDef.specialFieldItem = value ?? undefined;
        fieldDef.longAnswer = value?.itemId != null ? String(value.itemId) : "";
        this.onAnswerChange(fieldDef);
    }

    private async syncElectionPositionSpecialItem(fieldDef: IngestionFieldDefinition): Promise<void> {
        if (fieldDef.fieldType !== "ELECTION_POSITION") {
            return;
        }
        const raw = this.trimLongAnswer(fieldDef.longAnswer);
        if (!raw) {
            fieldDef.specialFieldItem = undefined;
            return;
        }
        const id = parseInt(raw, 10);
        if (!id || isNaN(id)) {
            fieldDef.specialFieldItem = undefined;
            return;
        }
        try {
            const rows = await ElectionApi.getPositionTitles([id]);
            const name = rows?.[0]?.itemName?.trim();
            fieldDef.specialFieldItem = {
                itemId: id,
                itemName: name && name.length > 0 ? name : `Position ${id}`,
                itemTypeId: undefined,
            };
        } catch (e) {
            fieldDef.specialFieldItem = { itemId: id, itemName: `Position ${id}`, itemTypeId: undefined };
        }
    }

    getElectionPositionReadonlyLabel(fieldDef: IngestionFieldDefinition): string {
        const fromItem = fieldDef.specialFieldItem?.itemName;
        if (fromItem) {
            return fromItem;
        }
        const raw = this.trimLongAnswer(fieldDef.longAnswer);
        if (!raw) {
            return "(No selection)";
        }
        const id = parseInt(raw, 10);
        if (isNaN(id)) {
            return raw;
        }
        return `Position ${id}`;
    }

    /**
     * Keeps effect-driven refresh from re-firing for the same identity transition: set last-seen ids and linked
     * signals together (same pattern as post–createSubmission inside {@link refresh}).
     */
    private syncLinkedSubmissionIdentity(submissionIdUuid: string, ingestionPointId?: number | null): void {
        this.lastSubmissionIdUuid = submissionIdUuid;
        this._submissionIdUuid.set(submissionIdUuid);
        if (ingestionPointId != null) {
            this.lastIngestionPointId = ingestionPointId;
            this._ingestionPointId.set(ingestionPointId);
        }
    }

    private async applyLinkedSubmissionContextAndRefresh(
        submissionIdUuid: string,
        ingestionPointId: number,
    ): Promise<void> {
        this.syncLinkedSubmissionIdentity(submissionIdUuid, ingestionPointId);
        await this.refresh();
    }

    loadPreviewConfig = async (): Promise<void> => {
        const ingestionPoint = this.previewIngestionPoint();
        const previewFieldDefinitions = this.previewFieldDefinitions();
        if (!ingestionPoint || !previewFieldDefinitions) {
            return;
        }

        this.isRefreshing = true;
        try {
            const discreteOptionsRaw = await CustomAttributeService.getAllDiscreteOptions();
            this.discreteOptionsByAttrId = {};
            for (const optionGroup of discreteOptionsRaw) {
                this.discreteOptionsByAttrId[optionGroup.itemId] = optionGroup.opt.map((option) => ({
                    itemId: option,
                    itemName: option,
                }));
            }

            this.ingestionPoint.set(ingestionPoint);
            this.showHideRules = this.previewShowHideRules() ?? [];
            this.validationRules = [];
            this.submission.set(undefined);

            const fieldDefinitions = S25Util.deepCopy(previewFieldDefinitions);
            this.initializeFieldDefinitionsForPreview(fieldDefinitions);
            this.fieldDefinitions.set(fieldDefinitions);
            this.ensureDiscreteOptionsForAllFields();

            this.origFieldHiddenState.clear();
            for (const fieldDef of fieldDefinitions) {
                if (fieldDef.fieldDefinitionId) {
                    this.origFieldHiddenState.set(fieldDef.fieldDefinitionId, fieldDef.hidden || false);
                }
            }

            this.runRules();
            this.init = true;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to load form preview:", error);
            S25Util.showError(error);
        } finally {
            setTimeout(() => {
                this.isRefreshing = false;
            }, 0);
        }
    };

    private initializeFieldDefinitionsForPreview(fieldDefinitions: IngestionFieldDefinition[]): void {
        for (const fieldDef of fieldDefinitions) {
            fieldDef.infoTextExpanded = fieldDef.infoTextExpanded ?? false;
            fieldDef.showInfoText = !!(fieldDef.infoText && fieldDef.infoTextExpanded);
            if (!fieldDef.answers) {
                fieldDef.answers = [];
            }
            if (!fieldDef.longAnswer) {
                fieldDef.longAnswer = fieldDef.fieldType === "TOGGLE" ? "false" : "";
            }
            if (!fieldDef.jsonFieldItem) {
                fieldDef.jsonFieldItem = {} as ObjectOccurrenceData | RegistrantsData;
            }
            if (
                fieldDef.answerType === AnswerType.BOOLEAN &&
                (fieldDef.fieldType === "SELECT" ||
                    fieldDef.fieldType === "MULTISELECT" ||
                    fieldDef.fieldType === "CHECKBOX" ||
                    fieldDef.fieldType === "RADIOBUTTON" ||
                    fieldDef.fieldType === "TEXT")
            ) {
                this.ensureBooleanOptions(fieldDef);
            }
        }
    }

    refresh = async () => {
        if (this.previewMode()) {
            return;
        }
        this.isRefreshing = true;
        const hostSubmissionIdUuid = this.submissionIdUuid();
        try {
            const submissionIdUuid = this._submissionIdUuid();
            const ingestionPointId = this._ingestionPointId();
            const openedExistingDraftFromHost =
                !!submissionIdUuid && !!hostSubmissionIdUuid && hostSubmissionIdUuid === submissionIdUuid;

            if (submissionIdUuid) {
                // Loading existing submission
                this.submission.set(await DataIngestionService.getSubmission(submissionIdUuid));
                if (!this.submission()) {
                    throw new Error("Submission not found");
                }

                this.submitted = !!this.submission()?.submitted;
                // sometimes the ingestion point id is not set bc the submission id is enough to get the ingestion point, so we need to set it here
                const resolvedPointId = this.submission()!.ingestionPointId!;
                this.syncLinkedSubmissionIdentity(submissionIdUuid, resolvedPointId);

                // Load the ingestion point, field definitions, and discrete options in parallel
                const [ingestionPoint, fieldDefinitions, answers, showHideRules, validationRules, discreteOptionsRaw] =
                    await Promise.all([
                        DataIngestionService.getIngestionPoint(resolvedPointId),
                        DataIngestionService.getFieldDefinitions(resolvedPointId),
                        DataIngestionService.getAnswers(submissionIdUuid),
                        RuleTreeService.getRulesBySubCategory("ingestion", resolvedPointId.toString(), true),
                        RuleTreeService.getRulesBySubCategory("ingestionValid", resolvedPointId.toString(), true),
                        CustomAttributeService.getAllDiscreteOptions(),
                    ]);
                this.discreteOptionsByAttrId = {};
                for (const o of discreteOptionsRaw) {
                    this.discreteOptionsByAttrId[o.itemId] = o.opt.map((opt) => ({
                        itemId: opt,
                        itemName: opt,
                    }));
                }

                this.ingestionPoint.set(ingestionPoint);
                this.showHideRules = showHideRules || [];
                this.validationRules = validationRules || [];

                await this.mapAnswersToFields(fieldDefinitions, answers);
                const defaultsApplied = await this.applyDefaultValues(fieldDefinitions);
                const hydrateDirty = await this.invokeRuntimeHydrateIfNeeded(fieldDefinitions);
                this.fieldDefinitions.set(fieldDefinitions);
                this.ensureDiscreteOptionsForAllFields();
                if (defaultsApplied || hydrateDirty) await this.saveProgress();
            } else if (ingestionPointId) {
                // Loading form for new submission
                const [ingestionPoint, fieldDefinitions, showHideRules, validationRules, discreteOptionsRaw] =
                    await Promise.all([
                        DataIngestionService.getIngestionPoint(ingestionPointId),
                        DataIngestionService.getFieldDefinitions(ingestionPointId),
                        RuleTreeService.getRulesBySubCategory("ingestion", ingestionPointId.toString(), true),
                        RuleTreeService.getRulesBySubCategory("ingestionValid", ingestionPointId.toString(), true),
                        CustomAttributeService.getAllDiscreteOptions(),
                    ]);
                this.discreteOptionsByAttrId = {};
                for (const o of discreteOptionsRaw) {
                    this.discreteOptionsByAttrId[o.itemId] = o.opt.map((opt) => ({
                        itemId: opt,
                        itemName: opt,
                    }));
                }

                this.ingestionPoint.set(ingestionPoint);
                this.showHideRules = showHideRules || [];
                this.validationRules = validationRules || [];

                // Create a new submission if we don't have one
                if (!this.submission()) {
                    const data: Partial<IngestionSubmission> = {
                        eventId: this.eventId(),
                        electionId: this.electionId(),
                    };
                    this.submission.set(await DataIngestionService.createSubmission(ingestionPointId, data));
                    // Update internal signals with the newly created submission ID
                    if (this.submission()?.submissionIdUuid) {
                        const createdSubmission = this.submission()!;
                        this.syncLinkedSubmissionIdentity(
                            createdSubmission.submissionIdUuid!,
                            createdSubmission.ingestionPointId,
                        );
                    }
                }

                const changed = await this.applyDefaultValues(fieldDefinitions);
                const hydrateDirty = await this.invokeRuntimeHydrateIfNeeded(fieldDefinitions);
                this.fieldDefinitions.set(fieldDefinitions);
                this.ensureDiscreteOptionsForAllFields();
                if (changed || hydrateDirty) await this.saveProgress();
            }

            // Store original hidden states
            this.origFieldHiddenState.clear();
            for (const fieldDef of this.fieldDefinitions()!) {
                if (fieldDef.fieldDefinitionId) {
                    this.origFieldHiddenState.set(fieldDef.fieldDefinitionId, fieldDef.hidden || false);
                }
            }

            // Initialize frontend convenience fields
            for (const fieldDef of this.fieldDefinitions()!) {
                fieldDef.infoTextExpanded = fieldDef.infoTextExpanded ?? false;
                fieldDef.showInfoText = !!(fieldDef.infoText && fieldDef.infoTextExpanded);
                if (!fieldDef.answers) {
                    fieldDef.answers = [];
                }
                if (!fieldDef.longAnswer) {
                    fieldDef.longAnswer = fieldDef.fieldType === "TOGGLE" ? "false" : "";
                }
                if (!fieldDef.jsonFieldItem) {
                    fieldDef.jsonFieldItem = {} as ObjectOccurrenceData | RegistrantsData;
                }
                if (
                    fieldDef.answerType === AnswerType.BOOLEAN &&
                    (fieldDef.fieldType === "SELECT" ||
                        fieldDef.fieldType === "MULTISELECT" ||
                        fieldDef.fieldType === "CHECKBOX" ||
                        fieldDef.fieldType === "RADIOBUTTON" ||
                        fieldDef.fieldType === "TEXT")
                ) {
                    this.ensureBooleanOptions(fieldDef);
                }
            }

            // Load files for FILE field types in parallel (using file_key)
            const submissionId = this.submission()?.submissionId;
            const fileFieldPromises =
                submissionId != null
                    ? this.fieldDefinitions()!
                          .filter((fieldDef) => fieldDef.fieldType === "FILE")
                          .map(async (fieldDef) => {
                              const fileKey = "fs&" + fieldDef.fieldDefinitionId + "&" + submissionId;
                              const fileItems = (await AttachmentService.getFileList(-1, -1, fileKey)) || [];
                              fieldDef.files = fileItems.map(AttachmentService.convertFileItemToAttachmentItem);
                          })
                    : [];

            await Promise.all(fileFieldPromises);

            // Rules already loaded above

            this.runRules();

            if (openedExistingDraftFromHost) {
                this.cancelShouldDeleteDraft = false;
                this.sessionBaselineAnswers = S25Util.deepCopy(this.collectAnswers());
            } else if (!hostSubmissionIdUuid) {
                this.cancelShouldDeleteDraft = true;
                this.sessionBaselineAnswers = null;
            }

            this.init = true;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to load form:", error);
            S25Util.showError(error);
        } finally {
            // Use setTimeout to ensure any effects triggered by signal changes during refresh
            // will still see isRefreshing = true and skip, then set to false in next tick
            setTimeout(() => {
                this.isRefreshing = false;
            }, 0);
        }
    };

    mapAnswersToFields = async (fieldDefinition: IngestionFieldDefinition[], answers: IngestionFieldAnswer[]) => {
        const answerMap = new Map<number, IngestionFieldAnswer[]>();

        // Group answers by field definition ID
        for (const answer of answers) {
            if (answer.fieldDefinitionId && !answerMap.has(answer.fieldDefinitionId)) {
                answerMap.set(answer.fieldDefinitionId, []);
            }
            if (answer.fieldDefinitionId) {
                answerMap.get(answer.fieldDefinitionId)?.push(answer);
            }
        }

        // Map answers to field definitions
        for (const fieldDef of fieldDefinition) {
            const fieldAnswers = fieldDef.fieldDefinitionId ? answerMap.get(fieldDef.fieldDefinitionId) || [] : [];
            fieldDef.answersMapped = fieldAnswers.length > 0;

            if (fieldDef.fieldType === "SELECT" || fieldDef.fieldType === "RADIOBUTTON") {
                if (fieldAnswers.length > 0) {
                    const answerValue = fieldAnswers[0].answerRaw || "";
                    if (fieldDef.answerType === AnswerType.BOOLEAN && fieldDef.fieldType === "RADIOBUTTON") {
                        fieldDef.longAnswer = answerValue;
                    } else {
                        fieldDef.singleAnswer = fieldDef.options?.find((opt) => opt.option === answerValue);
                    }
                }
            } else if (fieldDef.fieldType === "MULTISELECT" || fieldDef.fieldType === "CHECKBOX") {
                if (fieldDef.answerType === AnswerType.BOOLEAN && fieldDef.fieldType === "CHECKBOX") {
                    if (fieldAnswers.length > 0) {
                        fieldDef.longAnswer = fieldAnswers[0].answerRaw || "";
                    }
                } else {
                    fieldDef.answers = [];
                    for (const answer of fieldAnswers) {
                        const answerValue = answer.answerRaw || "";
                        const option = fieldDef.options?.find((opt) => opt.option === answerValue);
                        if (option) {
                            fieldDef.answers.push(option);
                        }
                    }
                }
            } else if (fieldDef.fieldType === "MULTISELECT_OCCURRENCES") {
                // TODO
                fieldDef.answers = fieldAnswers.map((answer) => ({ option: answer.answerRaw }));
            } else if (fieldDef.fieldType === "CATEGORIES") {
                // CATEGORIES stores multiple rows with category IDs as answerRaw
                const categoryIds = fieldAnswers.map((a) => parseInt(a.answerRaw || "0", 10)).filter((id) => !!id);
                if (!categoryIds.length) {
                    fieldDef.jsonFieldItem = { items: [] } as CategoriesData;
                } else {
                    // Fetch names for all category IDs at once
                    const categories = await SearchCriteriaService.getEventCategories();
                    const categoryMap = new Map<number, string>(
                        categories.map((cat: any) => [cat.itemId, String(cat.itemName || "")]),
                    );
                    const items = categoryIds.map((id) => ({
                        itemId: id,
                        itemName: categoryMap.get(id) || `Category ${id}`,
                    }));
                    fieldDef.jsonFieldItem = { items } as CategoriesData;
                }
            } else if (fieldDef.fieldType === "REQUIREMENTS") {
                const requirementIds = fieldAnswers.map((a) => parseInt(a.answerRaw || "0", 10)).filter((id) => !!id);
                if (!requirementIds.length) {
                    fieldDef.jsonFieldItem = { items: [] } as CategoriesData;
                } else {
                    const requirements = await SearchCriteriaService.getEventRequirements(false);
                    const reqMap = new Map<number, string>(
                        requirements.map((req: any) => [req.itemId, String(req.itemName || "")]),
                    );
                    const items = requirementIds.map((id) => ({
                        itemId: id,
                        itemName: reqMap.get(id) || `Requirement ${id}`,
                    }));
                    fieldDef.jsonFieldItem = { items } as CategoriesData;
                }
            } else if (fieldDef.fieldType === "ORGANIZATION_MULTISELECT") {
                const orgIds = fieldAnswers.map((a) => parseInt(a.answerRaw || "0", 10)).filter((id) => !!id);
                if (!orgIds.length) {
                    fieldDef.jsonFieldItem = { items: [] } as CategoriesData;
                } else {
                    const organizations = await OrganizationService.getOrganizationsMinimal(orgIds);
                    const items = orgIds.map((id) => {
                        const org = organizations.find((o: any) => o.itemId === id);
                        return { itemId: id, itemName: org?.itemName || `Organization ${id}` };
                    });
                    fieldDef.jsonFieldItem = { items } as CategoriesData;
                }
            } else if (fieldDef.fieldType === "ORG_CATEGORIES") {
                const categoryIds = fieldAnswers.map((a) => parseInt(a.answerRaw || "0", 10)).filter((id) => !!id);
                if (!categoryIds.length) {
                    fieldDef.jsonFieldItem = { items: [] } as CategoriesData;
                } else {
                    const categories = await SearchCriteriaService.getOrganizationCategories();
                    const categoryMap = new Map<number, string>(
                        categories.map((cat: any) => [cat.itemId, String(cat.itemName || "")]),
                    );
                    const items = categoryIds.map((id) => ({
                        itemId: id,
                        itemName: categoryMap.get(id) || `Category ${id}`,
                    }));
                    fieldDef.jsonFieldItem = { items } as CategoriesData;
                }
            } else if (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") {
                if (fieldAnswers.length > 0) {
                    const dataType = fieldDef.custAtrb?.atrbDataType;
                    fieldDef.answerType = AnswerType.fromAtrbDataType(dataType);
                    if (dataType === CustomAttributes.type.Text && fieldDef.custAtrb?.multiVal) {
                        fieldDef.answers = fieldAnswers
                            .map((a) => ({
                                option: S25Util.toStr(a.answerString ?? a.answerRaw) ?? "",
                            }))
                            .filter((a) => !!a.option);
                    } else {
                        const answer = fieldAnswers[0];
                        if (dataType === CustomAttributes.type.Boolean) {
                            fieldDef.longAnswer = String(S25Util.toBool(answer.answerBoolean));
                        } else if (
                            dataType === CustomAttributes.type.Integer ||
                            dataType === CustomAttributes.type.Image
                        ) {
                            fieldDef.longAnswer = S25Util.toStr(answer.answerInteger);
                        } else if (dataType === CustomAttributes.type.Float) {
                            fieldDef.longAnswer = S25Util.toStr(answer.answerFloat);
                        } else if (dataType === CustomAttributes.type.Text || dataType === CustomAttributes.type.URL) {
                            fieldDef.longAnswer = S25Util.toStr(answer.answerString ?? answer.answerRaw);
                        } else if (dataType === CustomAttributes.type.LongText) {
                            fieldDef.longAnswer = S25Util.toStr(answer.answerLargeString);
                        } else if (
                            dataType === CustomAttributes.type.Date ||
                            dataType === CustomAttributes.type.DateTime ||
                            dataType === CustomAttributes.type.Time ||
                            dataType === CustomAttributes.type.Duration
                        ) {
                            const dt = S25Util.toStr(answer.answerDatetime);
                            if (dataType === CustomAttributes.type.Date) {
                                fieldDef.longAnswer = dt?.split("T")[0] ?? "";
                            } else if (
                                dataType === CustomAttributes.type.Time ||
                                dataType === CustomAttributes.type.Duration
                            ) {
                                fieldDef.longAnswer = dt?.split("T")[1] ?? "";
                            } else {
                                fieldDef.longAnswer = dt;
                            }
                        } else {
                            fieldDef.longAnswer = S25Util.toStr(answer.answerRaw);
                        }
                    }
                }
            } else if (fieldDef.fieldType === "DURATION") {
                if (fieldAnswers.length > 0) {
                    fieldDef.longAnswer = fieldAnswers[0].answerDatetime || fieldAnswers[0].answerRaw || "";
                }
            } else if (fieldDef.fieldType === "TOGGLE") {
                if (fieldAnswers.length > 0) {
                    fieldDef.longAnswer = fieldAnswers[0].answerRaw || "";
                }
            } else if (this.isTextOrRichTextField(fieldDef)) {
                if (fieldAnswers.length > 0) {
                    const a = fieldAnswers[0];
                    fieldDef.longAnswer = a.answerLargeString || a.answerRaw || "";
                }
            } else if (fieldDef.fieldType === "IMAGE") {
                if (fieldAnswers.length > 0) {
                    const imageAnswer = fieldAnswers[0];
                    fieldDef.longAnswer =
                        imageAnswer.answerLargeString || imageAnswer.answerString || imageAnswer.answerRaw || "";
                }
            } else if (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") {
                // Stars rating and numeric scale store integer values as strings
                if (fieldAnswers.length > 0) {
                    fieldDef.longAnswer = fieldAnswers[0].answerRaw || "";
                }
            } else if (fieldDef.fieldType === "ELECTION_POSITION") {
                if (fieldAnswers.length > 0) {
                    fieldDef.longAnswer = fieldAnswers[0].answerRaw || "";
                }
                await this.syncElectionPositionSpecialItem(fieldDef);
            } else if (fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
                if (fieldAnswers.length > 0) {
                    const eventIdAnswer =
                        fieldAnswers[0].answerInteger ?? parseInt(fieldAnswers[0].answerRaw || "", 10);
                    if (!isNaN(eventIdAnswer) && eventIdAnswer) {
                        fieldDef.longAnswer = String(eventIdAnswer);
                    }
                }
            } else if (this.isJsonField(fieldDef)) {
                fieldDef.jsonFieldItem = await this.createJsonFieldItem(fieldDef, fieldAnswers[0]?.answerJson);
            } else if (this.isSpecialFieldType(fieldDef.fieldType!)) {
                // Handle special field types (EVENT_TYPE, ORGANIZATION, etc.)
                if (fieldAnswers.length > 0) {
                    const answerValue = fieldAnswers[0].answerRaw || "";
                    const itemId = parseInt(answerValue, 10);
                    if (!isNaN(itemId)) {
                        // Create DropDownItem object with itemId and resolved name
                        const item = await this.createSpecialFieldItem(fieldDef.fieldType!, itemId);
                        fieldDef.specialFieldItem = item;
                    }
                }
            }
        }
    };

    onAnswerChange = (fieldDef: IngestionFieldDefinition) => {
        if (this.readOnly()) {
            return;
        }
        this.ensureDiscreteOptionsForField(fieldDef);
        this.runRules();
        this.runFormValidationRules(); // Form validation might be affected by field changes
        this.autoSaveField(fieldDef);
    };

    onOccurrenceFieldChange = (fieldDef: IngestionFieldDefinition, event: ObjectOccurrenceData | undefined) => {
        if (this.readOnly()) {
            return;
        }
        fieldDef.jsonFieldItem = event;
        this.validateField(fieldDef);
        this.runRules();
        this.runFormValidationRules();
        this.autoSaveField(fieldDef);
    };

    runRules = () => {
        const fieldDefinitions = this.fieldDefinitions();
        if (!fieldDefinitions) return;
        // Resolve VARIANT to concrete AnswerType for rule evaluation (uses custAtrb.atrbDataType from API)
        for (const fieldDef of fieldDefinitions) {
            if (fieldDef.answerType === "VARIANT" && fieldDef.custAtrb?.atrbDataType) {
                fieldDef.answerType = AnswerType.fromAtrbDataType(fieldDef.custAtrb.atrbDataType) ?? AnswerType.STRING;
            }
        }
        // Run show/hide rules
        const satRules = EventFormRuleUtil.getSatisfiedRules({
            rules: this.showHideRules || [],
            ingestionFields: fieldDefinitions,
        });

        const fieldsWithAction = new Set<number>();
        satRules.sync.forEach((r) => {
            if (!r) return;
            const rule = S25RuleTreeUtil.parseRule(r);
            for (const [action, items] of Object.entries(rule.targets)) {
                if (action === "showField") {
                    items.forEach((item) => {
                        fieldDefinitions.forEach((f) => {
                            if (f.fieldDefinitionId === item.itemId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                                fieldsWithAction.add(f.fieldDefinitionId);
                                f.hidden = false;
                            }
                        });
                    });
                } else if (action === "hideField") {
                    items.forEach((item) => {
                        fieldDefinitions.forEach((f) => {
                            if (f.fieldDefinitionId === item.itemId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                                fieldsWithAction.add(f.fieldDefinitionId);
                                f.hidden = true;
                            }
                        });
                    });
                }
            }
        });

        // Reset fields that don't have rule actions to their original state
        fieldDefinitions.forEach((f) => {
            if (f.fieldDefinitionId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                f.hidden = this.origFieldHiddenState.get(f.fieldDefinitionId) || false;
            }
        });

        this.cd.detectChanges();
        if (!this.previewMode()) {
            this.saveProgress();
        }
    };

    /** Safely trim longAnswer; ngModel may bind numbers/dates to longAnswer for type=number/date inputs. */
    private trimLongAnswer = (val: unknown): string => {
        if (val instanceof Date) {
            return val.toISOString();
        }
        return String(val ?? "").trim();
    };

    /** TEXT and RICH_TEXT both store answers in `longAnswer` and share validation/collection behavior. */
    private isTextOrRichTextField(fieldDef: IngestionFieldDefinition): boolean {
        const t = fieldDef.fieldType;
        return t === "TEXT" || t === "RICH_TEXT";
    }

    shouldShowFieldCharCount(fieldDef: IngestionFieldDefinition): boolean {
        return shouldShowCharCount(resolveFieldDefForLength(fieldDef), this.isFieldReadOnly(fieldDef));
    }

    formatFieldCharCountMessage(fieldDef: IngestionFieldDefinition): string {
        const resolved = resolveFieldDefForLength(fieldDef);
        const maxLength = getEffectiveMaxLength(resolved)!;
        return formatCharCountRemaining(fieldDef.longAnswer, maxLength, fieldUsesRichTextLength(resolved));
    }

    isFieldAnswerOverCharLimit(fieldDef: IngestionFieldDefinition): boolean {
        const maxLength = getEffectiveMaxLength(resolveFieldDefForLength(fieldDef))!;
        return isOverCharLimit(fieldDef.longAnswer, maxLength);
    }

    validateField = (fieldDef: IngestionFieldDefinition) => {
        fieldDef.validationError = "";

        if (fieldDef.fieldType === "SECTION" || fieldDef.hidden) {
            return;
        }

        // LIVE_25_EVENT_FORM owns its own validation inside the embedded 25Live form;
        // the integer answer is only populated server-side after a successful draft save.
        if (fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
            return;
        }

        // Required validation
        if (fieldDef.required) {
            // SELECT field: must have a selected option
            if (fieldDef.fieldType === "SELECT") {
                if (!fieldDef.singleAnswer) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
            // RADIOBUTTON field: must have a selection
            else if (fieldDef.fieldType === "RADIOBUTTON") {
                // Boolean radiobutton stores value in longAnswer
                if (fieldDef.answerType === AnswerType.BOOLEAN) {
                    if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                        fieldDef.validationError = "This field is required";
                        return;
                    }
                }
                // Non-boolean radiobutton stores value in singleAnswer
                else {
                    if (!fieldDef.singleAnswer) {
                        fieldDef.validationError = "This field is required";
                        return;
                    }
                }
            }
            // MULTISELECT field: must have at least one selection
            else if (fieldDef.fieldType === "MULTISELECT" || fieldDef.fieldType === "MULTISELECT_OCCURRENCES") {
                if (!fieldDef.answers || fieldDef.answers.length === 0) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
            // CHECKBOX field: behavior depends on answerType
            else if (fieldDef.fieldType === "CHECKBOX") {
                // Boolean checkbox stores value in longAnswer
                if (fieldDef.answerType === AnswerType.BOOLEAN) {
                    if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                        fieldDef.validationError = "This field is required";
                        return;
                    }
                }
                // Multi-select checkbox stores values in answers array
                else {
                    if (!fieldDef.answers || fieldDef.answers.length === 0) {
                        fieldDef.validationError = "This field is required";
                        return;
                    }
                }
            }
            // TEXT / RICH_TEXT: must have a non-empty value
            else if (this.isTextOrRichTextField(fieldDef)) {
                if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
            // STARS_RATING and NUMERIC_SCALE fields: must have a value
            else if (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") {
                if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
            // CUSTOM_ATTRIBUTE field: must have a non-empty value
            else if (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") {
                if (fieldDef.custAtrb?.multiVal) {
                    if (!fieldDef.answers?.length) {
                        fieldDef.validationError = "This field is required";
                        return;
                    }
                } else if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (fieldDef.fieldType === "OBJECT_OCCURRENCE") {
                // Check if there's at least one occurrence
                const occurrenceData = fieldDef.jsonFieldItem as ObjectOccurrenceData | undefined;
                if (
                    !occurrenceData ||
                    !occurrenceData.occurrences ||
                    !Array.isArray(occurrenceData.occurrences) ||
                    occurrenceData.occurrences.length === 0
                ) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (
                fieldDef.fieldType === "CATEGORIES" ||
                fieldDef.fieldType === "REQUIREMENTS" ||
                fieldDef.fieldType === "ORGANIZATION_MULTISELECT" ||
                fieldDef.fieldType === "ORG_CATEGORIES"
            ) {
                const multiselectData = fieldDef.jsonFieldItem as { items?: DropDownItem[] } | undefined;
                if (!multiselectData?.items?.length) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (fieldDef.fieldType === "CONTACT_ROLE" || fieldDef.fieldType === "ORG_CONTACT_ROLE") {
                const contactRoleData = fieldDef.jsonFieldItem as ContactRoleData | undefined;
                if (!contactRoleData?.items?.length) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (fieldDef.fieldType === "DURATION") {
                if (!fieldDef.longAnswer) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (fieldDef.fieldType === "ELECTION_POSITION") {
                if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (fieldDef.fieldType === "IMAGE") {
                if (this.trimLongAnswer(fieldDef.longAnswer) === "") {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (this.isSpecialFieldType(fieldDef.fieldType!)) {
                // Check if there's a selected value (integer)
                if (!fieldDef.specialFieldItem || !fieldDef.specialFieldItem.itemId) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
        }

        // Only validate TEXT / RICH_TEXT fields - SELECT/MULTISELECT options are predefined and valid
        if (this.isTextOrRichTextField(fieldDef) && this.trimLongAnswer(fieldDef.longAnswer)) {
            const error = this.validateSingleValue(this.trimLongAnswer(fieldDef.longAnswer), fieldDef);
            if (error) {
                fieldDef.validationError = error;
                return;
            }
        }

        // Validate STARS_RATING and NUMERIC_SCALE fields for range
        if (
            (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") &&
            this.trimLongAnswer(fieldDef.longAnswer)
        ) {
            const error = this.validateSingleValue(this.trimLongAnswer(fieldDef.longAnswer), fieldDef);
            if (error) {
                fieldDef.validationError = error;
                return;
            }
        }

        // Validate CUSTOM_ATTRIBUTE fields based on their resolved data type
        if (
            (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") &&
            this.trimLongAnswer(fieldDef.longAnswer)
        ) {
            const resolvedAnswerType =
                fieldDef.answerType === AnswerType.VARIANT && fieldDef.custAtrb?.atrbDataType
                    ? (AnswerType.fromAtrbDataType(fieldDef.custAtrb.atrbDataType) ?? fieldDef.answerType)
                    : fieldDef.answerType;
            const error = this.validateSingleValue(this.trimLongAnswer(fieldDef.longAnswer), {
                ...fieldDef,
                answerType: resolvedAnswerType,
            });
            if (error) {
                fieldDef.validationError = error;
                return;
            }
        }

        this.cd.detectChanges();
    };

    validateSingleValue = (value: string, fieldDef: IngestionFieldDefinition): string | null => {
        // required validation is handled by the fieldDef.required check
        if (S25Util.isUndefined(value) || value.trim() === "") {
            return null;
        }

        const trimmedValue = value.trim();

        // Min/Max validation (numeric for INTEGER/FLOAT, length for strings)
        if (fieldDef.answerType === AnswerType.INTEGER || fieldDef.answerType === AnswerType.FLOAT) {
            const numValue = parseFloat(trimmedValue);
            if (isNaN(numValue)) {
                return "Invalid number";
            }
            if (fieldDef.minLength && numValue < fieldDef.minLength) {
                return `Minimum value is ${fieldDef.minLength}`;
            }
            if (fieldDef.maxLength && numValue > fieldDef.maxLength) {
                return `Maximum value is ${fieldDef.maxLength}`;
            }
        } else {
            // String length validation
            if (fieldDef.minLength && trimmedValue.length < fieldDef.minLength) {
                return `Minimum ${fieldDef.minLength} characters required`;
            }
            const maxLength = getEffectiveMaxLength(fieldDef);
            if (maxLength != null && trimmedValue.length > maxLength) {
                return `Maximum ${maxLength} characters allowed`;
            }
        }

        // Regex validation
        if (fieldDef.regexValid && fieldDef.regexValid.trim() !== "") {
            try {
                const regex = new RegExp(fieldDef.regexValid);
                if (!regex.test(trimmedValue)) {
                    return `Invalid format. Expected format: ${fieldDef.regexValid}`;
                }
            } catch (e) {
                console.warn("Invalid regex pattern:", fieldDef.regexValid);
                return `Invalid format. Expected format: ${fieldDef.regexValid}`;
            }
        }

        return null; // No validation errors
    };

    runFormValidationRules = () => {
        // Clear previous form validation errors
        this.formValidationErrors = [];

        if (!this.validationRules || this.validationRules.length === 0) {
            return;
        }

        // Run validation rules for the entire form
        const satRules = EventFormRuleUtil.getSatisfiedRules({
            rules: this.validationRules || [],
            ingestionFields: this.fieldDefinitions(),
        });

        // Collect form-level validation errors
        satRules.sync.forEach((r) => {
            if (!r) return;
            const rule = S25RuleTreeUtil.parseRule(r);
            for (const [action, items] of Object.entries(rule.targets)) {
                if (action === "validateIngestion") {
                    items.forEach((item) => {
                        if (item.itemValue) {
                            // Add to form-level validation errors instead of field-level
                            this.formValidationErrors.push(item.itemValue);
                        }
                    });
                }
            }
        });

        this.cd.detectChanges();
    };

    getInputType = (answerType: string | undefined): string => {
        switch (answerType) {
            case "INTEGER":
                return "number";
            case "FLOAT":
                return "number";
            case "DATETIME":
                return "datetime-local";
            default:
                return "text";
        }
    };

    /** Shared image preview: mode 'id' = type I (longAnswer is imageId), mode 'url' = -20/-21 (longAnswer is URL string). */
    getImagePreviewUrl = (fieldDef: IngestionFieldDefinition, mode: "id" | "url"): string | undefined => {
        const raw = this.trimLongAnswer(fieldDef.longAnswer);
        if (!raw) return undefined;
        if (mode === "url") return raw;
        const imageId = parseInt(raw, 10);
        if (!imageId || isNaN(imageId)) return undefined;
        return S25ImageService.getUrl(imageId);
    };

    /** Shared image upload: mode 'id' = store imageId (type I), mode 'url' = store URL (e.g. -20/-21). */
    onImageUploaded = async (fieldDef: IngestionFieldDefinition, base64DataUri: string, mode: "id" | "url") => {
        if (!base64DataUri || this.readOnly()) return;
        // Ignore preview URLs (from [imageUri] binding) - only process actual base64 image data
        if (base64DataUri.startsWith("http://") || base64DataUri.startsWith("https://")) return;
        const fileName = "ingestion-image-" + Date.now() + ".png";
        const imageId = await ImageService.postImage(
            fieldDef.fieldLabel || "Ingestion image",
            "",
            fileName,
            S25Util.extractBase64FromURI(base64DataUri),
            1,
        );
        if (imageId != null) {
            fieldDef.longAnswer = mode === "id" ? String(imageId) : S25ImageService.getUrl(imageId);
            this.onAnswerChange(fieldDef);
            this.cd.detectChanges();
        }
    };

    getCustomAttrImagePreview = (fieldDef: IngestionFieldDefinition): string | undefined =>
        this.getImagePreviewUrl(fieldDef, "id");

    onCustomAttrImageUploaded = async (fieldDef: IngestionFieldDefinition, base64DataUri: string) =>
        this.onImageUploaded(fieldDef, base64DataUri, "id");

    clearCustomAttrImage = (fieldDef: IngestionFieldDefinition) => {
        fieldDef.longAnswer = "";
        this.onAnswerChange(fieldDef);
        this.cd.detectChanges();
    };

    getUrlImagePreview = (fieldDef: IngestionFieldDefinition): string | undefined =>
        this.getImagePreviewUrl(fieldDef, "url");

    onUrlImageUploaded = async (fieldDef: IngestionFieldDefinition, base64DataUri: string) =>
        this.onImageUploaded(fieldDef, base64DataUri, "url");

    getUrlFileUploadHandler = (fieldDef: IngestionFieldDefinition) => (file: S25File) =>
        this.onUrlFileUpload(fieldDef, file);

    onUrlFileUpload = async (fieldDef: IngestionFieldDefinition, file: S25File) => {
        if (this.readOnly()) return;
        const submissionId = this.submission()?.submissionId;
        if (submissionId == null || !fieldDef.fieldDefinitionId) return;
        const fileName = file.itemName ?? file.name ?? "";
        if (!fileName) {
            S25Util.showError("File name is required");
            return;
        }
        const fileKey = "fs&" + fieldDef.fieldDefinitionId + "&" + submissionId;
        await AttachmentService.postFile(-1, -1, file.fileRef, fileName, undefined, fileKey);
        fieldDef.longAnswer = FileStoreUtil.buildFileStoreValue(fileKey, fileName);
        this.onAnswerChange(fieldDef);
        this.cd.detectChanges();
    };

    sanitizedUrl = (value: string | undefined): string => {
        if (!value) return "";
        return value.startsWith("http://") || value.startsWith("https://") ? value : "http://" + value;
    };

    getBooleanOptions = (fieldDef: IngestionFieldDefinition): IngestionFieldOption[] => {
        // Ensure Yes/No options exist for boolean fields (stored as "true"/"false")
        if (!fieldDef.options || fieldDef.options.length === 0) {
            return [
                { option: "true", fieldDefinitionId: fieldDef.fieldDefinitionId },
                { option: "false", fieldDefinitionId: fieldDef.fieldDefinitionId },
            ];
        }
        // If options exist, ensure we have true/false values
        const hasTrue = fieldDef.options.some((opt) => opt.option === "true");
        const hasFalse = fieldDef.options.some((opt) => opt.option === "false");
        if (!hasTrue || !hasFalse) {
            const options = [...fieldDef.options];
            if (!hasTrue) {
                options.push({ option: "true", fieldDefinitionId: fieldDef.fieldDefinitionId });
            }
            if (!hasFalse) {
                options.push({ option: "false", fieldDefinitionId: fieldDef.fieldDefinitionId });
            }
            return options;
        }
        return fieldDef.options;
    };

    getBooleanOptionLabel = (fieldDef: IngestionFieldDefinition): string => {
        const isTrue = fieldDef.longAnswer === "true" || fieldDef.longAnswer === "1";
        return isTrue ? "Yes" : "No";
    };

    getBooleanDisplayLabel = (fieldDef: IngestionFieldDefinition, optionValue?: string): string => {
        const value = optionValue ?? fieldDef.longAnswer ?? "";
        return value === "true" ? "Yes" : value === "false" ? "No" : value;
    };

    // Helper method to parse integer for use in template expressions
    parseInt = (value: string | number | undefined | null, radix: number = 10): number => {
        if (value === undefined || value === null) return 0;
        return S25Util.parseInt(value, radix);
    };

    ensureBooleanOptions = (fieldDef: IngestionFieldDefinition) => {
        // Get options with true/false ensured (may include other options)
        const optionsWithBooleans = this.getBooleanOptions(fieldDef);
        // Filter to only keep boolean options and assign back to fieldDef
        fieldDef.options = optionsWithBooleans.filter((opt) => opt.option === "true" || opt.option === "false");
    };

    // Clamped min/max for stars and numeric scale (defensive for legacy data)
    getScaleMin = (fieldDef: IngestionFieldDefinition): number =>
        Math.max(STARS_NUMERIC_SCALE_MIN, fieldDef.minLength ?? STARS_NUMERIC_SCALE_MIN);
    getScaleMax = (fieldDef: IngestionFieldDefinition): number =>
        Math.min(STARS_NUMERIC_SCALE_MAX, fieldDef.maxLength ?? STARS_NUMERIC_SCALE_MAX);

    // Helper methods for stars rating
    getStarsArray = (fieldDef: IngestionFieldDefinition): number[] => {
        const maxStars = this.getScaleMax(fieldDef);
        return Array.from({ length: maxStars }, (_, i) => i);
    };

    getStarsRating = (fieldDef: IngestionFieldDefinition): number => {
        if (!fieldDef.longAnswer) return 0;
        const rating = parseInt(fieldDef.longAnswer, 10);
        return isNaN(rating) ? 0 : rating;
    };

    setStarsRating = (fieldDef: IngestionFieldDefinition, rating: number) => {
        fieldDef.longAnswer = String(rating);
        this.onAnswerChange(fieldDef);
    };

    // Helper methods for numeric scale
    getNumericScaleValue = (fieldDef: IngestionFieldDefinition): number => {
        const minVal = this.getScaleMin(fieldDef);
        if (!fieldDef.longAnswer) {
            return minVal;
        }
        const value = parseInt(fieldDef.longAnswer, 10);
        return isNaN(value) ? minVal : value;
    };

    setNumericScaleValue = (fieldDef: IngestionFieldDefinition, value: string | number) => {
        const numValue = typeof value === "string" ? parseInt(value, 10) : value;
        if (!isNaN(numValue)) {
            fieldDef.longAnswer = String(numValue);
            this.onAnswerChange(fieldDef);
        }
    };

    getScaleTickArray = (fieldDef: IngestionFieldDefinition): number[] => {
        const min = this.getScaleMin(fieldDef);
        const max = this.getScaleMax(fieldDef);
        const ticks: number[] = [];
        for (let i = min; i <= max; i++) {
            ticks.push(i);
        }
        return ticks;
    };

    isOptionSelected = (fieldDef: IngestionFieldDefinition, option: IngestionFieldOption): boolean => {
        if (!fieldDef.answers) {
            return false;
        }
        return fieldDef.answers.some((ans) => ans.option === option.option);
    };

    onCheckboxOptionChange = (
        fieldDef: IngestionFieldDefinition,
        option: IngestionFieldOption,
        isSelected: boolean,
    ) => {
        if (!fieldDef.answers) {
            fieldDef.answers = [];
        }
        if (isSelected) {
            // Add option if not already in answers
            if (!fieldDef.answers.find((ans) => ans.option === option.option)) {
                fieldDef.answers.push(option);
            }
        } else {
            // Remove option from answers
            fieldDef.answers = fieldDef.answers.filter((ans) => ans.option !== option.option);
        }
        this.onAnswerChange(fieldDef);
    };

    scrollToFirstError = () => {
        // First check for form-level validation errors (they appear at the top)
        if (this.formValidationErrors && this.formValidationErrors.length > 0 && this.formErrorContainer) {
            this.formErrorContainer.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        // Then check for field-level validation errors using ViewChildren for error elements
        if (this.validationErrors && this.validationErrors.length > 0) {
            const firstError = this.validationErrors.first;
            if (firstError) {
                firstError.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    };

    autoSaveField = S25Util.debounce(async (fieldDef: IngestionFieldDefinition) => {
        if (this.previewMode() || this.readOnly() || !this.submission() || this.submission()!.submitted) {
            return;
        }
        await this.saveFieldProgress(fieldDef);
    }, 300);

    saveFieldProgress = async (fieldDef: IngestionFieldDefinition) => {
        if (
            this.previewMode() ||
            this.readOnly() ||
            !this.submission() ||
            this.submission()!.submitted ||
            fieldDef.fieldType === "SECTION"
        ) {
            return;
        }

        await this.saveSemaphore.wait();

        if (this.submission()!.submitted) return;

        // Validate field client-side first
        this.validateField(fieldDef);

        // Don't save if validation failed
        if (fieldDef.validationError) {
            this.cd.detectChanges();
            return;
        }

        this.saving = true;
        this.saveSemaphore.acquire();
        this.cd.detectChanges();

        try {
            // Use consolidated logic to collect answers for this single field
            const answers = this.collectAnswersForFields([fieldDef]);

            // Use saveAnswersForField which deletes only answers for this specific field
            await DataIngestionService.saveAnswersForField(
                this._submissionIdUuid()!,
                fieldDef.fieldDefinitionId!,
                answers,
            );
        } catch (error) {
            console.error("Failed to save field progress:", error);
            fieldDef.validationError = "Failed to save field progress: " + S25Util.errorText(error);
        } finally {
            this.saving = false;
            this.saveSemaphore.release();
            this.cd.detectChanges();
        }
    };

    saveProgress = async (): Promise<boolean> => {
        if (this.previewMode()) {
            return false;
        }
        const fieldDefinitions = this.fieldDefinitions();
        if (this.readOnly() || !this.submission() || this.submission()!.submitted || !fieldDefinitions) {
            return false;
        }

        await this.saveSemaphore.wait();

        if (this.submission()!.submitted) {
            return false;
        }

        this.saving = true;
        this.saveSemaphore.acquire();
        this.cd.detectChanges();

        try {
            const answers = this.collectAnswers();

            // Validate only the fields that have answers we're about to save
            const fieldsWithAnswers = new Set(answers.map((a) => a.fieldDefinitionId));
            for (const fieldDef of fieldDefinitions) {
                if (fieldsWithAnswers.has(fieldDef.fieldDefinitionId)) {
                    this.validateField(fieldDef);
                    // Don't save progress if any field with answers has validation errors
                    if (fieldDef.validationError) {
                        this.cd.detectChanges();
                        return false;
                    }
                }
            }

            await DataIngestionService.saveAnswers(this._submissionIdUuid()!, answers);
            return true;
        } catch (error) {
            console.error("Failed to save progress:", error);
            S25Util.showError(error);
            return false;
        } finally {
            this.saving = false;
            this.saveSemaphore.release();
            this.cd.detectChanges();
        }
    };

    validateAndSubmit = async () => {
        if (this.previewMode()) {
            return;
        }
        const fieldDefinitions = this.fieldDefinitions();
        if (this.readOnly() || !this.submission() || this.submission()!.submitted || !fieldDefinitions) {
            return;
        }

        await this.saveSemaphore.wait();

        if (this.submission()!.submitted) return;

        // Clear all validation errors first
        for (const fieldDef of fieldDefinitions) {
            fieldDef.validationError = "";
        }

        // Validate all fields (primitive validation only)
        let hasErrors = false;
        for (const fieldDef of fieldDefinitions) {
            this.validateField(fieldDef);
            if (fieldDef.validationError) {
                hasErrors = true;
            }
        }

        // Run custom validation rules at form level
        this.runFormValidationRules();

        // Check for form-level validation errors
        if (this.formValidationErrors && this.formValidationErrors.length > 0) {
            hasErrors = true;
        }

        if (hasErrors) {
            this.cd.detectChanges();
            this.scrollToFirstError();
            return;
        }

        this.saving = true;
        this.saveSemaphore.acquire();
        this.cd.detectChanges();

        try {
            // Drive the embedded 25Live event-form draft save before persisting Connect25 answers,
            // since the LIVE_25_EVENT_FORM integer answer (and the OBJECT_OCCURRENCE snapshot
            // emitted by collectAnswersForFields) come from that save.
            await this.persistEmbeddedLive25EventForm();

            const answers = this.collectAnswers();
            const submissionIdUuid = this._submissionIdUuid()!;
            await DataIngestionService.saveAnswers(submissionIdUuid, answers);

            // Submit the form (all validation is client-side)
            await DataIngestionService.submitSubmission(submissionIdUuid);

            // Only mark the submission submitted after the server-side submit succeeds.
            // Previously these flags lived in `finally`, which flipped the UI to "submitted"
            // even when an embedded 25Live save or DataIngestionService call threw.
            this.submitted = true;
            this.submission()!.submitted = true;

            // Emit onSubmit event for parent component
            this.onSubmit.emit({
                submissionIdUuid: submissionIdUuid,
                answers,
                successMessage: this.ingestionPoint()?.successMessage,
            });
        } catch (error) {
            console.error("Failed to submit form:", error);
            // The embedded 25Live event form already surfaces validation / quota / conflict /
            // availability / putEvent failures itself. Skip the duplicate Connect25 toast for
            // those; let other (unexpected) errors fall through to the normal handler.
            if (!isEmbeddedEventFormHandledError(error)) {
                S25Util.showError(error);
            }
        } finally {
            this.saving = false;
            this.saveSemaphore.release();
            this.cd.detectChanges();
        }
    };

    saveEditedAnswers = async () => {
        const fieldDefinitions = this.fieldDefinitions();
        if (!this.submission() || !fieldDefinitions) return;

        await this.saveSemaphore.wait();

        for (const fieldDef of fieldDefinitions) {
            fieldDef.validationError = "";
        }

        let hasErrors = false;
        for (const fieldDef of fieldDefinitions) {
            this.validateField(fieldDef);
            if (fieldDef.validationError) hasErrors = true;
        }

        this.runFormValidationRules();
        if (this.formValidationErrors?.length) hasErrors = true;

        if (hasErrors) {
            this.cd.detectChanges();
            this.scrollToFirstError();
            return;
        }

        this.saving = true;
        this.saveSemaphore.acquire();
        this.cd.detectChanges();

        try {
            await this.persistEmbeddedLive25EventForm();
            const answers = this.collectAnswers();
            const submissionIdUuid = this._submissionIdUuid()!;
            const result = await DataIngestionService.editSubmittedAnswers(submissionIdUuid, answers);
            this.editSaveMessage.set(result?.message || "Changes saved successfully.");
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to save edited answers:", error);
            // Same UX contract as validateAndSubmit: 25Live event form owns its own messaging.
            if (!isEmbeddedEventFormHandledError(error)) {
                S25Util.showError(error);
            }
        } finally {
            this.saving = false;
            this.saveSemaphore.release();
            this.cd.detectChanges();
        }
    };

    openForkSeedModal = () => {
        this.forkSeedModalOpen.set(true);
        this.forkUseAnotherForm.set(false);
        this.forkTargetFormSelection.set(null);
        this.cd.detectChanges();
    };

    closeForkSeedModal = () => {
        this.forkSeedModalOpen.set(false);
    };

    onForkSeedBackdropClick = (event: MouseEvent) => {
        if (event.target === event.currentTarget) {
            this.closeForkSeedModal();
        }
    };

    onForkSameFormChoice = () => {
        this.forkUseAnotherForm.set(false);
        this.forkTargetFormSelection.set(null);
        this.cd.detectChanges();
    };

    onForkOtherFormChoice = () => {
        this.forkUseAnotherForm.set(true);
        this.forkTargetFormSelection.set(null);
        this.cd.detectChanges();
    };

    confirmForkSeed = async () => {
        const submission = this.submission();
        if (!submission?.submissionIdUuid) {
            return;
        }
        const targetIngestionPointId = this.forkUseAnotherForm()
            ? (this.forkTargetFormSelection()?.itemId ?? this.forkTargetFormSelection()?.val)
            : submission.ingestionPointId;
        if (targetIngestionPointId == null) {
            return;
        }
        this.forkSeedBusy.set(true);
        this.cd.detectChanges();
        try {
            const created = await DataIngestionService.forkSubmissionToNewDraft({
                sourceSubmissionIdUuid: submission.submissionIdUuid,
                targetIngestionPointId,
            });
            const newUuid = created.submissionIdUuid;
            const newPointId = created.ingestionPointId;
            if (!newUuid || newPointId == null) {
                throw new Error("New draft was created but is missing submission id or ingestion point.");
            }
            this.cancelShouldDeleteDraft = true;
            this.sessionBaselineAnswers = null;
            await this.applyLinkedSubmissionContextAndRefresh(newUuid, newPointId);
            const reloaded = this.submission();
            if (reloaded) {
                this.forkContextCreated.emit(reloaded);
            }
            this.closeForkSeedModal();
        } catch (error) {
            console.error("Fork submission failed:", error);
            S25Util.showError(error);
        } finally {
            this.forkSeedBusy.set(false);
            this.cd.detectChanges();
        }
    };

    cancelForm = async () => {
        await this.confirmAndDiscardDraft();
    };

    shouldConfirmDiscardOnLeave(): boolean {
        if (this.previewMode() || this.readOnly()) {
            return false;
        }
        const submission = this.submission();
        if (!submission?.submissionIdUuid) {
            return false;
        }
        if (submission.submitted || this.submitted) {
            return false;
        }
        return true;
    }

    async confirmAndDiscardDraft(): Promise<boolean> {
        const confirmMessage = this.cancelShouldDeleteDraft
            ? "Discard this form? Your answers will be lost and the draft will be removed."
            : "Discard unsaved changes? Your answers will revert to what was saved when you opened this form.";
        if (!(await AlertService.confirm(confirmMessage))) {
            return false;
        }
        try {
            await this.applyDiscardDraft();
            this.onClose.emit();
            return true;
        } catch (error) {
            console.error(this.cancelShouldDeleteDraft ? "Failed to discard form:" : "Failed to revert form:", error);
            S25Util.showError(error);
            return false;
        }
    }

    private async applyDiscardDraft(): Promise<void> {
        await this.saveSemaphore.wait();
        if (this.cancelShouldDeleteDraft) {
            await this.deleteSubmission();
        } else {
            await this.revertSessionChanges();
        }
    }

    private revertSessionChanges = async (): Promise<void> => {
        const submissionIdUuid = this._submissionIdUuid();
        const baseline = this.sessionBaselineAnswers;
        if (!submissionIdUuid || !baseline) {
            throw new Error("Nothing to revert");
        }
        this.saving = true;
        this.saveSemaphore.acquire();
        this.cd.detectChanges();
        try {
            await DataIngestionService.saveAnswers(submissionIdUuid, S25Util.deepCopy(baseline));
            await this.refresh();
        } finally {
            this.saving = false;
            this.saveSemaphore.release();
            this.cd.detectChanges();
        }
    };

    saveProgressOnly = async () => {
        await this.saveProgress();
    };

    saveProgressAndClose = async () => {
        const saved = await this.saveProgress();
        if (saved) {
            this.onClose.emit();
        }
    };

    toggleInfoText = (fieldDef: IngestionFieldDefinition) => {
        fieldDef.showInfoText = !fieldDef.showInfoText;
        this.cd.detectChanges();
    };

    clearAllSelections = (fieldDef: IngestionFieldDefinition) => {
        if (fieldDef.fieldType === "MULTISELECT" || fieldDef.fieldType === "MULTISELECT_OCCURRENCES") {
            fieldDef.answers = [];
            this.onAnswerChange(fieldDef);
        }
    };

    // Helper method to collect answers for a single field or all fields
    private collectAnswersForFields = (fieldDefs: IngestionFieldDefinition[]): IngestionFieldAnswer[] => {
        const answers: IngestionFieldAnswer[] = [];

        for (const fieldDef of fieldDefs) {
            if (!fieldDef?.fieldType) continue;
            if (fieldDef.fieldType === "SECTION") {
                continue;
            }
            // OBJECT_OCCURRENCE_SNAPSHOT (-4) is a hidden, derived system field. Its canonical
            // value is the embedded 25Live event form's current model, not the field def's
            // stale `jsonFieldItem` (which is `{ occurrences: [] }` on initial load and would
            // otherwise serialize as an empty snapshot).
            if (fieldDef.fieldDefinitionId === INGESTION_SYSTEM_FIELDS.OBJECT_OCCURRENCE_SNAPSHOT) {
                const wrapper = this.liveEventFormWrappers?.first;
                if (!wrapper) continue;
                const occurrenceData = extractObjectOccurrenceSnapshot(wrapper.getFormModel());
                const snapshotAnswer = this.newAnswer(fieldDef, JSON.stringify(occurrenceData));
                snapshotAnswer.answerJson = JSON.stringify(occurrenceData);
                answers.push(snapshotAnswer);
                continue;
            }

            if (fieldDef.fieldType === "SELECT" && fieldDef.singleAnswer?.option) {
                answers.push(this.newAnswer(fieldDef, fieldDef.singleAnswer.option));
            } else if (fieldDef.fieldType === "RADIOBUTTON") {
                if (fieldDef.answerType === AnswerType.BOOLEAN && fieldDef.longAnswer) {
                    answers.push(this.newAnswer(fieldDef, fieldDef.longAnswer));
                } else if (fieldDef.singleAnswer?.option) {
                    answers.push(this.newAnswer(fieldDef, fieldDef.singleAnswer.option));
                }
            } else if (
                fieldDef.fieldType === "MULTISELECT" ||
                fieldDef.fieldType === "MULTISELECT_OCCURRENCES" ||
                (fieldDef.fieldType === "CHECKBOX" && fieldDef.answerType !== AnswerType.BOOLEAN)
            ) {
                for (const option of fieldDef.answers || []) {
                    answers.push(this.newAnswer(fieldDef, option.option));
                }
            } else if (
                fieldDef.fieldType === "CHECKBOX" &&
                fieldDef.answerType === AnswerType.BOOLEAN &&
                fieldDef.longAnswer
            ) {
                answers.push(this.newAnswer(fieldDef, fieldDef.longAnswer));
            } else if (fieldDef.fieldType === "TOGGLE" && fieldDef.longAnswer) {
                answers.push(this.newAnswer(fieldDef, fieldDef.longAnswer));
            } else if (this.isTextOrRichTextField(fieldDef) && this.trimLongAnswer(fieldDef.longAnswer)) {
                answers.push(this.newAnswer(fieldDef, this.trimLongAnswer(fieldDef.longAnswer)));
            } else if (
                (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") &&
                this.trimLongAnswer(fieldDef.longAnswer)
            ) {
                // Stars rating and numeric scale store integer values as strings in longAnswer
                answers.push(this.newAnswer(fieldDef, this.trimLongAnswer(fieldDef.longAnswer)));
            } else if (fieldDef.fieldType === "ELECTION_POSITION" && this.trimLongAnswer(fieldDef.longAnswer)) {
                answers.push(this.newAnswer(fieldDef, this.trimLongAnswer(fieldDef.longAnswer)));
            } else if (fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
                const eventIdRaw = this.trimLongAnswer(fieldDef.longAnswer);
                const eventIdParsed = eventIdRaw ? parseInt(eventIdRaw, 10) : NaN;
                if (!isNaN(eventIdParsed) && eventIdParsed) {
                    const answer = this.newAnswer(fieldDef, String(eventIdParsed));
                    answer.answerInteger = eventIdParsed;
                    answers.push(answer);
                }
            } else if (fieldDef.fieldType === "DURATION" && fieldDef.longAnswer) {
                answers.push(this.newAnswer(fieldDef, fieldDef.longAnswer));
            } else if (fieldDef.fieldType === "FILE") {
                const fileName = fieldDef.files?.[0]?.itemName;
                const fileKey = this.imageFieldFileKey(fieldDef);
                if (fileName && fileKey) {
                    answers.push(this.newAnswer(fieldDef, FileStoreUtil.buildFileStoreValue(fileKey, fileName)));
                } else {
                    const typedUrl = this.trimLongAnswer(fieldDef.longAnswer);
                    answers.push(this.newAnswer(fieldDef, typedUrl || ""));
                }
            } else if (fieldDef.fieldType === "IMAGE" && this.trimLongAnswer(fieldDef.longAnswer)) {
                answers.push(this.newAnswer(fieldDef, this.trimLongAnswer(fieldDef.longAnswer)));
            } else if (
                fieldDef.fieldType === "CATEGORIES" ||
                fieldDef.fieldType === "REQUIREMENTS" ||
                fieldDef.fieldType === "ORGANIZATION_MULTISELECT" ||
                fieldDef.fieldType === "ORG_CATEGORIES"
            ) {
                const multiselectData = fieldDef.jsonFieldItem as { items?: DropDownItem[] } | undefined;
                for (const item of multiselectData?.items || []) {
                    answers.push(this.newAnswer(fieldDef, String(item.itemId)));
                }
            } else if (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") {
                const fieldAnswers = fieldDef.answers;
                if (fieldDef.custAtrb?.multiVal && (fieldAnswers?.length ?? 0) > 0 && fieldAnswers) {
                    for (const a of fieldAnswers) {
                        if (a?.option) answers.push(this.newAnswer(fieldDef, a.option));
                    }
                } else {
                    const raw = this.trimLongAnswer(fieldDef.longAnswer);
                    answers.push(this.newAnswer(fieldDef, raw || undefined));
                }
            } else if (this.isJsonField(fieldDef)) {
                const field: IngestionFieldDefinition = { ...fieldDef, jsonFieldItem: undefined };
                let jsonFieldItem = { ...fieldDef.jsonFieldItem } as any;
                if (fieldDef.fieldType === "REGISTRANTS") {
                    const registrantsData = jsonFieldItem as RegistrantsData;
                    registrantsData.items = registrantsData.items.map((item) => ({
                        itemId: item.itemId,
                    }));
                } else if (fieldDef.fieldType === "CONTACT_ROLE" || fieldDef.fieldType === "ORG_CONTACT_ROLE") {
                    const contactRoleData = jsonFieldItem as ContactRoleData;
                    jsonFieldItem = {
                        items: (contactRoleData.items || []).map((item) => ({
                            roleId: item.roleId,
                            contactId: item.contactId,
                        })),
                    };
                }
                answers.push(this.newAnswer(field, JSON.stringify(jsonFieldItem)));
            } else if (this.isSpecialFieldType(fieldDef.fieldType)) {
                // Handle special field types (EVENT_TYPE, ORGANIZATION, etc.)
                if (!!fieldDef.specialFieldItem?.itemId) {
                    answers.push(this.newAnswer(fieldDef, fieldDef.specialFieldItem.itemId.toString()));
                }
            }
        }

        return answers;
    };

    collectAnswers = (): IngestionFieldAnswer[] => {
        return this.collectAnswersForFields(this.fieldDefinitions()!);
    };

    /** Same `file_key` pattern as {@link getFileModel} for submission-scoped filestore uploads. */
    imageFieldFileKey(fieldDef: IngestionFieldDefinition): string | undefined {
        if (!fieldDef?.fieldDefinitionId) return undefined;
        const submissionId = this.submission()?.submissionId;
        if (!submissionId) return undefined;
        return "fs&" + fieldDef.fieldDefinitionId + "&" + submissionId;
    }

    getFileModel = (fieldDef: IngestionFieldDefinition) => {
        const fileKey = this.imageFieldFileKey(fieldDef);
        if (!fileKey) return undefined;
        return {
            files: fieldDef.files || [],
            itemTypeId: -1,
            itemId: -1,
            addAction: async (file: S25File) => {
                await AttachmentService.postFile(-1, -1, file.fileRef, file.itemName, undefined, fileKey);
                const fileItems =
                    (await AttachmentService.getFileList(-1, -1, fileKey))?.map(
                        AttachmentService.convertFileItemToAttachmentItem,
                    ) || [];
                fieldDef.files = fileItems;
                await this.saveFieldProgress(fieldDef);
                this.onAnswerChange(fieldDef);
                this.cd.detectChanges();
            },
            removeAction: async (file: S25File) => {
                await AttachmentService.delFile(-1, -1, file.itemName, fileKey);
                const fileItems =
                    (await AttachmentService.getFileList(-1, -1, fileKey))?.map(
                        AttachmentService.convertFileItemToAttachmentItem,
                    ) || [];
                fieldDef.files = fileItems;
                await this.saveFieldProgress(fieldDef);
                this.onAnswerChange(fieldDef);
                this.cd.detectChanges();
            },
            showLinks: true,
        };
    };

    private async invokeRuntimeHydrateIfNeeded(fieldDefinitions: IngestionFieldDefinition[]): Promise<boolean> {
        if (this.readOnly()) {
            return false;
        }
        const hydrate = this.runtimeHydrate();
        if (!hydrate) {
            return false;
        }
        const outcome = await hydrate({
            fieldDefinitions,
            submission: this.submission,
            ingestionPoint: this.ingestionPoint(),
        });
        return outcome === true;
    }

    private async applyDefaultValues(fieldDefinition: IngestionFieldDefinition[]): Promise<boolean> {
        let hasDefaultValues = false;

        for (const fieldDef of fieldDefinition) {
            if (fieldDef.answersMapped) continue;

            if (!fieldDef.defaultValue) {
                if (this.isJsonField(fieldDef)) {
                    fieldDef.jsonFieldItem = await this.createJsonFieldItem(fieldDef, undefined);
                }
                continue;
            }

            if (
                fieldDef.fieldType === "SELECT" ||
                (fieldDef.fieldType === "RADIOBUTTON" && fieldDef.answerType !== AnswerType.BOOLEAN)
            ) {
                // Find matching option for default value
                fieldDef.singleAnswer = fieldDef.options?.find((opt) => opt.option === fieldDef.defaultValue);
                if (fieldDef.singleAnswer) {
                    hasDefaultValues = true;
                }
            } else if (fieldDef.fieldType === "RADIOBUTTON" && fieldDef.answerType === AnswerType.BOOLEAN) {
                // For boolean radiobutton, set longAnswer
                if (fieldDef.defaultValue === "true" || fieldDef.defaultValue === "false") {
                    fieldDef.longAnswer = fieldDef.defaultValue;
                    hasDefaultValues = true;
                }
            } else if (
                fieldDef.fieldType === "MULTISELECT" ||
                fieldDef.fieldType === "MULTISELECT_OCCURRENCES" ||
                (fieldDef.fieldType === "CHECKBOX" && fieldDef.answerType !== AnswerType.BOOLEAN)
            ) {
                // Split default value by | and find matching options
                const defaultValues = S25Util.array.unique(
                    fieldDef.defaultValue
                        .split("|")
                        .map((val) => val.trim())
                        .filter((val) => val),
                ); // Remove empty strings

                if (defaultValues.length > 0) {
                    fieldDef.answers = [];
                    for (const defaultValue of defaultValues) {
                        const defaultOption = fieldDef.options?.find((opt) => opt.option === defaultValue);
                        if (defaultOption) {
                            fieldDef.answers.push(defaultOption);
                        }
                    }
                    if (fieldDef.answers.length > 0) {
                        hasDefaultValues = true;
                    }
                }
            } else if (fieldDef.fieldType === "CHECKBOX" && fieldDef.answerType === AnswerType.BOOLEAN) {
                // For boolean checkbox, set longAnswer
                if (fieldDef.defaultValue === "true" || fieldDef.defaultValue === "false") {
                    fieldDef.longAnswer = fieldDef.defaultValue;
                    hasDefaultValues = true;
                }
            } else if (fieldDef.fieldType === "TOGGLE") {
                if (fieldDef.defaultValue === "true" || fieldDef.defaultValue === "false") {
                    fieldDef.longAnswer = fieldDef.defaultValue;
                    hasDefaultValues = true;
                }
            } else if (this.isTextOrRichTextField(fieldDef)) {
                // Set default value for text / rich text fields
                fieldDef.longAnswer = fieldDef.defaultValue;
                if (this.trimLongAnswer(fieldDef.longAnswer)) {
                    hasDefaultValues = true;
                }
            } else if (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") {
                // Set default value for stars rating and numeric scale fields
                const defaultValue = parseInt(fieldDef.defaultValue || "", 10);
                if (!isNaN(defaultValue)) {
                    // Validate default value is within min/max range (clamped)
                    const min = this.getScaleMin(fieldDef);
                    const max = this.getScaleMax(fieldDef);
                    if (defaultValue >= min && defaultValue <= max) {
                        fieldDef.longAnswer = String(defaultValue);
                        hasDefaultValues = true;
                    }
                }
            } else if (fieldDef.fieldType === "ELECTION_POSITION" && fieldDef.defaultValue) {
                const pid = parseInt(fieldDef.defaultValue, 10);
                if (!isNaN(pid)) {
                    fieldDef.longAnswer = String(pid);
                    hasDefaultValues = true;
                    await this.syncElectionPositionSpecialItem(fieldDef);
                }
            } else if (this.isJsonField(fieldDef)) {
                const [defaultValue] = S25Util.parseJson(fieldDef.defaultValue);
                if (defaultValue) {
                    fieldDef.jsonFieldItem = await this.createJsonFieldItem(fieldDef, fieldDef.defaultValue);
                    hasDefaultValues = true;
                }
            } else if (this.isSpecialFieldType(fieldDef.fieldType!) && fieldDef.defaultValue) {
                // For special field types (EVENT_TYPE, ORGANIZATION, etc.), default value should be an itemId
                const hasDefault = await this.setSpecialFieldDefaultValue(fieldDef);
                if (hasDefault) {
                    hasDefaultValues = true;
                }
            }
        }

        return hasDefaultValues;
    }

    // Configuration for special field types
    private static readonly SPECIAL_FIELD_CONFIG = {
        EVENT_TYPE: {
            searchCriteriaType: "eventTypes",
            itemTypeId: 7,
            getNameMethod: async (itemId: number) => {
                const eventType = await MasterDefinitionService.getById("event_type", itemId);
                return eventType?.type_name || `Event Type ${itemId}`;
            },
        },
        ORGANIZATION: {
            searchCriteriaType: "organizations",
            itemTypeId: 2,
            getNameMethod: async (itemId: number) => {
                return (await OrganizationService.getOrganizationName(itemId)) || `Organization ${itemId}`;
            },
        },
        CONTACT: {
            searchCriteriaType: "contacts",
            itemTypeId: Item.Ids.Contact,
            getNameMethod: async (itemId: number) => {
                const contacts = await ContactService.getContactsNameAndFormal([itemId]);
                const contact = contacts.find((c) => c.itemId === itemId);
                return contact?.itemName || `Contact ${itemId}`;
            },
        },
        ROLE: {
            searchCriteriaType: "organizationRoles",
            itemTypeId: undefined as number | undefined,
            getNameMethod: async (itemId: number) => {
                return ""; // loaded by dropdown
            },
        },
        ORG_TYPE: {
            searchCriteriaType: "organizationTypes",
            itemTypeId: undefined as number | undefined,
            getNameMethod: async (itemId: number) => {
                return ""; // loaded by dropdown
            },
        },
        FORM: {
            searchCriteriaType: "ingestionPoints",
            itemTypeId: 0,
            getNameMethod: async (itemId: number) => {
                return (await DataIngestionService.getIngestionPointName(itemId)) || `Form ${itemId}`;
            },
        },
        EVENT: {
            searchCriteriaType: "events",
            itemTypeId: Item.Ids.Event,
            getNameMethod: async (itemId: number) => {
                return (await NameService.getName(itemId, Item.Ids.Event)) || `Event ${itemId}`;
            },
        },
        ORG_RATING: {
            searchCriteriaType: "organizationRatings",
            itemTypeId: undefined as number | undefined,
            getNameMethod: async (itemId: number) => {
                return ""; // loaded by dropdown
            },
        },
        SECURITY_GROUP: {
            searchCriteriaType: "securityGroups",
            itemTypeId: undefined as number | undefined,
            getNameMethod: async (itemId: number) => {
                return ""; // loaded by dropdown
            },
        },
    } as const;

    // Helper method to create DropDownItem object for special field types
    private async createSpecialFieldItem(fieldType: string, itemId: number): Promise<DropDownItem> {
        try {
            const config = (IngestionFormComponent.SPECIAL_FIELD_CONFIG as any)[fieldType];
            if (!config) {
                return {
                    itemId: itemId,
                    itemName: `Item ${itemId}`,
                    itemTypeId: 0,
                };
            }

            const itemName = await config.getNameMethod(itemId);
            return {
                itemId: itemId,
                itemName: itemName,
                itemTypeId: config.itemTypeId,
            };
        } catch (error) {
            console.warn(`Failed to load ${fieldType} name for itemId ${itemId}:`, error);
            return {
                itemId: itemId,
                itemName: `${fieldType} ${itemId}`,
                itemTypeId: 0,
            };
        }
    }

    fieldControlId(fieldDef: IngestionFieldDefinition): string {
        return `ingestion-form-${this.instanceId}-field-${fieldDef.fieldDefinitionId}`;
    }

    fieldLabelId(fieldDef: IngestionFieldDefinition): string {
        return `ingestion-form-${this.instanceId}-label-${fieldDef.fieldDefinitionId}`;
    }

    usesFieldsetLabel(fieldDef: IngestionFieldDefinition): boolean {
        return (
            fieldDef.fieldType === "CHECKBOX" ||
            fieldDef.fieldType === "RADIOBUTTON" ||
            fieldDef.fieldType === "STARS_RATING" ||
            fieldDef.fieldType === "MULTISELECT_OCCURRENCES"
        );
    }

    // Helper method to check if a field type is a special field type
    isSpecialFieldType(fieldType: string | undefined): boolean {
        return !!fieldType && fieldType in IngestionFormComponent.SPECIAL_FIELD_CONFIG;
    }

    isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return DataIngestionService.isJsonField(fieldDef);
    }

    // Helper method to get search criteria type for special field types
    getSearchCriteriaType(fieldType: string | undefined): SearchCriteriaType["type"] | undefined {
        if (!fieldType) return undefined;
        const config = (IngestionFormComponent.SPECIAL_FIELD_CONFIG as any)[fieldType];
        return config?.searchCriteriaType || undefined;
    }

    private newAnswer(fieldDef: IngestionFieldDefinition, answerRaw: string | undefined): IngestionFieldAnswer {
        return {
            fieldDefinitionId: fieldDef.fieldDefinitionId,
            fieldDefinition: { ...fieldDef, specialFieldItem: undefined } as IngestionFieldDefinition,
            contId: this.contId,
            ingestionPointId: this._ingestionPointId(),
            submissionId: this.submission()?.submissionId,
            answerRaw: answerRaw,
        };
    }

    // Helper method to handle special field default value setting
    private async setSpecialFieldDefaultValue(fieldDef: IngestionFieldDefinition): Promise<boolean> {
        if (!this.isSpecialFieldType(fieldDef.fieldType!) || !fieldDef.defaultValue) {
            return false;
        }

        const itemId = parseInt(fieldDef.defaultValue, 10);
        if (isNaN(itemId)) {
            return false;
        }

        const item = await this.createSpecialFieldItem(fieldDef.fieldType!, itemId);
        fieldDef.specialFieldItem = item;
        this.cd.detectChanges();
        return true;
    }

    private async createJsonFieldItem(fieldDef: IngestionFieldDefinition, json: string | undefined) {
        const [jsonFieldItem] = S25Util.parseJson<IngestionFieldDefinition["jsonFieldItem"]>(json);

        if (fieldDef.fieldType === "REGISTRANTS") {
            const registrantsData: RegistrantsData = jsonFieldItem
                ? (jsonFieldItem as RegistrantsData)
                : { type: "everyone", items: [] };
            if (registrantsData.type === "everyone") return registrantsData;
            if (registrantsData.type === "users") return registrantsData;

            const criteriaType: SearchCriteriaType["type"] =
                registrantsData.type === "orgRoles" ? "organizationRoles" : registrantsData.type;
            await this.itemService
                .criteria(criteriaType)
                .fetchAll(registrantsData.items, { fetch: { name: true }, hydrate: true });
        } else if (fieldDef.fieldType === "CONTACT_ROLE" || fieldDef.fieldType === "ORG_CONTACT_ROLE") {
            const contactRoleData: ContactRoleData = jsonFieldItem ? (jsonFieldItem as ContactRoleData) : { items: [] };
            if (contactRoleData.items?.length) {
                await Promise.all(
                    contactRoleData.items.map((item) =>
                        Promise.all([
                            this.itemService
                                .criteria(fieldDef.fieldType === "CONTACT_ROLE" ? "eventRoles" : "organizationRoles")
                                .fetch(item.roleId)
                                .then((role) => (item.roleName = role.itemName)),
                            this.itemService
                                .criteria("contacts")
                                .fetch(item.contactId)
                                .then((contact) => (item.contactName = contact.itemName)),
                        ]),
                    ),
                );
            }
            return contactRoleData;
        }

        if (!jsonFieldItem) return {} as Exclude<IngestionFieldDefinition["jsonFieldItem"], RegistrantsData>;
        return jsonFieldItem as Exclude<IngestionFieldDefinition["jsonFieldItem"], RegistrantsData>;
    }

    onCategoriesChange = (fieldDef: IngestionFieldDefinition, items: S25ItemI[]) => {
        fieldDef.jsonFieldItem = { items: items || [] } as CategoriesData;
        this.onAnswerChange(fieldDef);
    };

    onRequirementsChange = (fieldDef: IngestionFieldDefinition, items: S25ItemI[]) => {
        fieldDef.jsonFieldItem = { items: items || [] } as CategoriesData;
        this.onAnswerChange(fieldDef);
    };

    onOrgMultiselectChange = (fieldDef: IngestionFieldDefinition, items: S25ItemI[]) => {
        fieldDef.jsonFieldItem = { items: items || [] } as CategoriesData;
        this.onAnswerChange(fieldDef);
    };

    onContactRoleChange = (fieldDef: IngestionFieldDefinition, value: ContactRoleData) => {
        fieldDef.jsonFieldItem = value;
        this.onAnswerChange(fieldDef);
    };

    onDurationChange = (fieldDef: IngestionFieldDefinition, value: string) => {
        fieldDef.longAnswer = value;
        this.onAnswerChange(fieldDef);
    };

    onMultiselectOccsChange(fieldDef: IngestionFieldDefinition) {
        fieldDef.answers = this.occurrenceOptions()!
            .filter((option) => option.checked)
            .map((option) => ({ option: option.option }));
        this.onAnswerChange(fieldDef);
    }

    selectAllOccurrences(fieldDef: IngestionFieldDefinition) {
        const occs = this.occurrenceOptions() ?? [];
        for (const occ of occs) occ.checked = true;
        this.onMultiselectOccsChange(fieldDef);
    }

    async deleteSubmission() {
        const submissionIdUuid = this._submissionIdUuid();
        if (!submissionIdUuid) return;
        await DataIngestionService.deleteSubmission(submissionIdUuid);
    }

    protected readonly Item = Item;
    protected readonly StateNames = S25States.names;
}
